"use client";

import clsx from "clsx";
import type { Session } from "next-auth";
import { getSession } from "next-auth/react";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import {
	type FormEventHandler,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { AddonInputType, type Addon, type BasketAddonItem, type BasketItem, type Package, type Service } from "@/types";
import { Collapsible, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { CollapsibleContent } from "@/components/ui/collapsible";
import { FeatureStatus, PlanStatus, type Feature, type Plan, UserPlan } from "@/types/planTypes";
import { serializeAddonForOrderApi } from "@/lib/addon-checkout";
import {
	getHostingPlanTier,
	hostingTierCoversCount,
	HOSTING_CUSTOM_PLAN_SUPPORT_MESSAGE,
	hostingExceedsProPlanMax,
	isExistingHostingTierUpgrade,
	type HostingPlanTier,
} from "@/lib/hosting/hosting-plan-tier.util";
import { calculatePackageTotal, getDefaultSelectedPackage, storefrontPackagesForService } from "@/lib/logic";
import { Discount } from "@/lib/constant";
import { addToBasket } from "@/features/basket/basketSlice";
import { invalidateApiSessionCache } from "@/app/api";
import { useHostingUsage } from "@/lib/hooks/useHostingUsage";
import { useCreateOrderMutation, useCreateGuestOrderMutation } from "@/features/order/orderApiInjection";
import { formatCurrency, formatCurrencyExact, type Currency } from "@/lib/utils/currency";
import { useCurrency } from "@/lib/contexts/CurrencyContext";
import { interpolateAddonDisplayTags, formatNumberAddonPerUnitSubline } from "@/lib/addon-display";

export { formatNumberAddonPerUnitSubline };
import { expressDeliveryDaysFromPackageDueDays } from "@/lib/utils/express-delivery.util";
import { isSafeStripeUrl } from "@/lib/security/safe-href";
import { resolveClientDeadlineMs, SERVICE_COUNTDOWN_ENDED_GRACE_MS } from "@/hooks/useServiceCountdownOfferOverlay";
import {
	ga4AddToCart,
	ga4ViewItem,
	goToStripeAfterCheckout,
	ga4SelectHostingAddon,
	ga4CtaClick,
	buildCommerceItemFromBasketLine,
} from "@/lib/analytics/ga4-ecommerce";
import { getPlans, isPremiumSubscriptionPlanMember } from "@/lib/api/plan";
import { useGetCategoriesQuery } from "@/features/categories/categoryApiInjection";
import {
	buildMainCategoryHostingEnabledMap,
	serviceCategoryShowsHosting,
} from "@/lib/service-hosting";

import { Button } from "@/components/ui/button";
import { getCtaDataAttrs } from "@/lib/analytics/cta-attrs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { showAddedToBasketToast } from "@/lib/basket/show-added-to-basket-toast";
import { RadioGroup2, RadioGroupItem2 } from "@/components/ui/radio-group-2";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PricingSummaryInfoDialog } from "@/components/pricing/PricingSummaryInfoDialog";
import { pricingSummaryInfoBtnClass } from "@/components/pricing/pricing-summary-info-icon";
import InfoIcon from "@/components/icons/Info";
import PromoCodeRedeemField from "@/components/promo/PromoCodeRedeemField";
import type { AppliedPromoCode } from "@/lib/promo-code";
import { applyPromosToInstallments, resolvePackageTierFromTitle } from "@/lib/promo-code";
import SignupEntryCard from "@/components/auth/SignupEntryCard";
import LoginCard from "@/components/auth/LoginCard";
import EmailSignUpCard from "@/components/auth/EmailSignUpCard";
import { cn } from "@/lib/utils";
import { Check, Clock3, XIcon, ChevronUp, ChevronDown, Minus, Plus } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";

export { serviceCategoryShowsHosting } from "@/lib/service-hosting";

export type SelectedAddon = BasketAddonItem & { id: string };

export type PackageFeature = {
	id: string;
	name: string;
	status: FeatureStatus;
	description?: string;
};

function ServiceSignupModal({
	open,
	onOpenChange,
	returnUrl,
	onVerified,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	returnUrl?: string;
	onVerified?: () => void | Promise<void>;
}) {
	const [mode, setMode] = useState<"signup" | "login" | "emailSignup">("signup");
	const handleOpenChange = (next: boolean) => {
		onOpenChange(next);
		if (!next) setMode("signup");
	};
	const handleAuthenticated = async () => {
		handleOpenChange(false);
		await onVerified?.();
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				className="max-w-md border-none bg-transparent p-0 shadow-none"
				closeClassName="text-muted-foreground hover:text-muted-foreground"
				onInteractOutside={(event) => {
					if (mode === "emailSignup") event.preventDefault();
				}}
			>
				<DialogTitle className="sr-only">
					{mode === "login" ? "Log in to continue checkout" : "Sign up to continue checkout"}
				</DialogTitle>
				<DialogDescription className="sr-only">
					Create a Techxeed account before buying this service.
				</DialogDescription>
				{mode === "login" ? (
					<LoginCard
						variant="modal"
						returnUrlOverride={returnUrl ?? "/"}
						onSwitchToSignup={() => setMode("signup")}
						onAuthenticated={handleAuthenticated}
					/>
				) : mode === "emailSignup" ? (
					<EmailSignUpCard
						variant="modal"
						returnUrlOverride={returnUrl ?? "/"}
						hideAvatarUpload
						onBack={() => setMode("signup")}
						onVerified={onVerified}
					/>
				) : (
					<SignupEntryCard
						returnUrl={returnUrl}
						showOneClickSeparator
						onSwitchToLogin={() => setMode("login")}
						onSwitchToEmailSignup={() => setMode("emailSignup")}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
}

function hostingPlanFeatureToPackageFeature(feature: Feature, index: number, groupKey: string): PackageFeature {
	const desc = feature.description?.replace(/\s+/g, " ").trim();
	return {
		id: feature._id ?? `${groupKey}-${index}`,
		name: feature.name,
		status: feature.status,
		description: desc && desc.length > 0 ? desc : undefined,
	};
}

function isToggleInputType(inputType: Addon["inputType"] | string | undefined): boolean {
	if (inputType === AddonInputType.toggle) return true;
	return typeof inputType === "string" && inputType.toUpperCase() === AddonInputType.toggle;
}

function isNumberInputType(inputType: Addon["inputType"] | string | undefined): boolean {
	if (inputType === AddonInputType.number) return true;
	return typeof inputType === "string" && inputType.toUpperCase() === AddonInputType.number;
}

/** Toggle add-on rendered as the express row (`ExpressDeliveryToggle`). */
export function isExpressDeliveryToggleAddon(
	addon: Pick<Addon, "inputType" | "description" | "unit">,
): boolean {
	if (!isToggleInputType(addon.inputType)) return false;
	const u = addon.unit ?? "";
	const d = addon.description ?? "";
	return (
		/express/i.test(d) ||
		/express/i.test(u) ||
		/\{time\}/i.test(u) ||
		/\{estimated delivery days of the package\}/i.test(u) ||
		/fast-track/i.test(d)
	);
}

const POSITIVE_VALUES = new Set(["yes", "true", "included", "available", "enabled"]);
const NEGATIVE_VALUES = new Set([
	"no",
	"false",
	"not included",
	"unavailable",
	"disabled",
	"none",
]);

const INSTALLMENT_THRESHOLD = 2000;
const SERVICE_CHECKOUT_RETURN_PARAM = "txServiceCheckout";
const SERVICE_CHECKOUT_STORAGE_PREFIX = "tx-service-checkout";
const SERVICE_CHECKOUT_MAX_AGE_MS = 30 * 60 * 1000;

type PendingServicePageCheckout = {
	serviceId: string;
	packageId: string;
	addons: SelectedAddon[];
	selectedPaymentOption: "full" | "installments" | "trial";
	selectedPremiumPlan: boolean;
	hostingPlanId: string | null;
	hostingBillingCycle: "monthly" | "yearly";
	createdAt: number;
};

const PAY_REST_NEXT_MONTH_INFO =
	"Remaining payment will be due in 30 days; billed automatically.";
const PAY_OVER_NEXT_TWO_MONTHS_INFO =
	"Next installment will be due in 30 days, and the final installment will be due in 60 days; billed automatically.";
const SAVE_TEN_PERCENT_PAY_IN_FULL_INFO = "Save an extra 10% on package prices.";
const FREE_TRIAL_14_DAYS_INFO =
	"No charge today. The full amount will be billed automatically after 14 days. You can cancel at any time before then.";
const DISCOUNTS_INFO =
	"This section outlines the discounts available to you. Any applicable discounts will be automatically applied to the package prices.";

const LICENSE_PRICE_INFO =
	"This one-time licence fee covers the licence for this platform you're currently viewing. The licence grants you lifetime usage rights for a single project, including access to the full source code.";

const PACKAGE_INFO = `This is the package you have selected above. It is a one-time fee to set up, customize and launch the platform you are currently viewing.

Standard package includes deployment, customization services, including content changes, layout adjustments, feature modifications, or design changes.

Premium package includes everything in the Standard package plus an additional 10hr customization service, Google Analytics set up, SEO optimization and custom integrations.`;

export const HOSTING_INFO =
	"The Starter plan includes hosting for 1 website only. If you plan to purchase multiple platforms through Techxeed, the Growth plan is recommended, as it allows you to host up to 3 platforms under a single hosting plan. If you intend to host more than 3 platforms, the Pro plan is recommended and supports up to 8 platforms.";

/** Match CMS hosting plan names (order = pill order). Entry tier: Starter or Basic. "Pro" also accepts "Professional". */
const HOSTING_PLAN_NAME_ALIASES: readonly (readonly string[])[] = [
	["starter", "basic"],
	["growth"],
	["pro", "professional"],
];

export function pickHostingPlansForServicePills(plans: Plan[]): Plan[] {
	const byName = new Map(plans.map((p) => [p.name.trim().toLowerCase(), p]));
	const seen = new Set<string>();
	const out: Plan[] = [];
	for (const aliases of HOSTING_PLAN_NAME_ALIASES) {
		let found: Plan | undefined;
		for (const key of aliases) {
			found = byName.get(key);
			if (found) break;
		}
		if (found && !seen.has(found._id)) {
			out.push(found);
			seen.add(found._id);
		}
	}
	return out;
}

export type { HostingPlanTier } from "@/lib/hosting/hosting-plan-tier.util";
export { getHostingPlanTier, hostingTierCoversCount, isExistingHostingTierUpgrade } from "@/lib/hosting/hosting-plan-tier.util";

function isHostingPlanBlockedForUsage(plan: Plan, totalHostingEligibleCount: number): boolean {
	const tier = getHostingPlanTier(plan);
	if (tier === "unknown") return false;
	if (totalHostingEligibleCount > 8) return true;
	if (totalHostingEligibleCount >= 4) return tier === "starter" || tier === "growth";
	if (totalHostingEligibleCount >= 2) return tier === "starter";
	return false;
}

function pickPreferredHostingPlanForUsage(plans: Plan[], totalHostingEligibleCount: number): string | null {
	const allowed = plans.filter((p) => !isHostingPlanBlockedForUsage(p, totalHostingEligibleCount));
	if (allowed.length === 0) return null;
	const idForTier = (tier: "starter" | "growth" | "pro") =>
		allowed.find((p) => getHostingPlanTier(p) === tier)?._id ?? null;
	if (totalHostingEligibleCount >= 4) return idForTier("pro") ?? allowed[0]._id;
	if (totalHostingEligibleCount >= 2) return idForTier("growth") ?? idForTier("pro") ?? allowed[0]._id;
	return idForTier("starter") ?? idForTier("growth") ?? allowed[0]._id;
}

export function getHostingPlanPriceForDuration(
	plan: Plan,
	currency: Currency,
	duration: UserPlan.monthly | UserPlan.yearly,
): number | undefined {
	const row = plan.prices?.find((p) => p.duration === duration);
	if (!row) return undefined;
	const fromAmounts = row.amounts?.[currency];
	if (typeof fromAmounts === "number" && Number.isFinite(fromAmounts) && fromAmounts > 0) {
		return fromAmounts;
	}
	const legacy = row.amount;
	if (typeof legacy === "number" && Number.isFinite(legacy) && legacy > 0) return legacy;
	return undefined;
}

export type HostingBillingCycle = "monthly" | "yearly";

export function HostingBillingCycleToggle({
	value,
	onChange,
	yearlyAvailable,
}: {
	value: HostingBillingCycle;
	onChange: (next: HostingBillingCycle) => void;
	/** False when no loaded hosting plan has a YEARLY price row (hide/disable yearly). */
	yearlyAvailable: boolean;
}) {
	return (
		<div className="flex w-full flex-col items-center gap-1">
			<div
				className="flex w-full max-w-[200px] rounded-full bg-[#e9eef2] p-[3px] dark:bg-[#080F14]"
				role="tablist"
				aria-label="Hosting billing period"
			>
				<button
					type="button"
					role="tab"
					aria-selected={value === "monthly"}
					onClick={() => {
						if (value === "monthly") return;
						ga4CtaClick({
							cta_id: "hosting_billing_monthly_package",
							cta_name: "Monthly",
							cta_location: "service_package_hosting",
						});
						onChange("monthly");
					}}
					className={cn(
						"flex h-[18px] min-h-0 flex-1 items-center justify-center rounded-full px-1.5 py-0 text-xs font-normal leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-1",
						value === "monthly"
							? "bg-secondary text-secondary-foreground shadow-sm"
							: "text-grey-foreground dark:text-muted-foreground",
					)}
				>
					Monthly
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={value === "yearly"}
					disabled={!yearlyAvailable}
					onClick={() => {
						if (!yearlyAvailable || value === "yearly") return;
						ga4CtaClick({
							cta_id: "hosting_billing_yearly_package",
							cta_name: "Yearly",
							cta_location: "service_package_hosting",
						});
						onChange("yearly");
					}}
					className={cn(
						"flex h-[18px] min-h-0 flex-1 items-center justify-center rounded-full px-1.5 py-0 text-xs font-normal leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-1",
						value === "yearly"
							? "bg-secondary text-secondary-foreground shadow-sm"
							: "text-grey-foreground dark:text-muted-foreground",
						!yearlyAvailable && "cursor-not-allowed opacity-45",
					)}
				>
					Yearly
				</button>
			</div>
			{value === "yearly" && yearlyAvailable ? (
				<p className="px-1 text-center text-xs font-normal leading-tight text-secondary">
					Save an extra 10% with annual billing
				</p>
			) : null}
		</div>
	);
}

export function HostingPlanOptionButton({
	plan,
	selected,
	onSelect,
	currency,
	billingCycle,
	disabled = false,
	footerNotice,
}: {
	plan: Plan;
	selected: boolean;
	onSelect: () => void;
	currency: Currency;
	billingCycle: HostingBillingCycle;
	/** When true, plan cannot be selected; specs may still expand. */
	disabled?: boolean;
	/** Short note shown under the pill (e.g. basket size rules). */
	footerNotice?: string;
}) {
	const [specsOpen, setSpecsOpen] = useState(false);
	const duration = billingCycle === "yearly" ? UserPlan.yearly : UserPlan.monthly;
	const amount = getHostingPlanPriceForDuration(plan, currency, duration);
	const periodSuffix = billingCycle === "yearly" ? "/year" : "/month";
	const visiblySelected = selected && !disabled;

	const handleSelectPlan = () => {
		if (disabled) return;
		onSelect();
		ga4SelectHostingAddon({
			plan_id: plan._id,
			plan_name: plan.name,
			billing_cycle: billingCycle,
		});
	};

	return (
		<div
			className={cn(
				"flex w-full min-w-0 max-w-full flex-col overflow-hidden rounded-[26px] border transition-all",
				disabled && "opacity-[0.58]",
				visiblySelected
					? "border-tertiary bg-white shadow-[0_14px_28px_rgba(23,199,184,0.25)] dark:bg-card dark:text-secondary"
					: "border-transparent bg-[#e9eef2] text-grey-foreground hover:border-[#dfe4e9] hover:bg-white dark:bg-[#080F14] dark:text-muted-foreground dark:hover:bg-core-midnight",
			)}
		>
			<Collapsible open={specsOpen} onOpenChange={setSpecsOpen} className="w-full min-w-0">
				<div className="flex w-full min-w-0 items-center gap-2 py-2 pl-[15px] pr-2 sm:gap-2 sm:py-2.5 sm:pl-5 sm:pr-2.5">
					<button
						type="button"
						onClick={handleSelectPlan}
						role="radio"
						aria-checked={visiblySelected}
						aria-disabled={disabled}
						disabled={disabled}
						className={cn(
							"flex min-h-0 min-w-0 flex-1 items-center gap-3 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2",
							disabled && "cursor-not-allowed",
						)}
					>
						<span
							className={cn(
								"flex h-4 w-4 shrink-0 items-center justify-center rounded-full border aspect-square",
								visiblySelected ? "border-tertiary bg-tertiary" : "border-grey/30 bg-white dark:bg-black",
								disabled && "opacity-80",
							)}
						/>
						<div
							className={cn(
								"min-w-0 text-sm font-semibold leading-tight",
								visiblySelected ? "text-secondary" : "text-grey-foreground dark:text-muted-foreground",
							)}
						>
							<span className="break-words">{plan.name}</span>
						</div>
					</button>
					<div
						className={cn(
							"inline-flex min-h-0 shrink-0 items-center gap-px py-0.5",
							!disabled && "cursor-pointer",
							disabled && "cursor-default",
						)}
						onClick={handleSelectPlan}
					>
						{amount !== undefined ? (
							<span className="inline-flex items-center gap-x-1 leading-none">
								<span
									className={cn(
										"whitespace-nowrap text-sm font-semibold tabular-nums",
										visiblySelected ? "text-secondary" : "text-grey-foreground dark:text-muted-foreground",
									)}
								>
									{formatCurrency(amount, currency)}
								</span>
								<span
									className={cn(
										"whitespace-nowrap text-xs font-normal tabular-nums",
										visiblySelected ? "text-secondary" : "text-grey-foreground dark:text-muted-foreground",
									)}
								>
									{periodSuffix}
								</span>
							</span>
						) : (
							<span className="text-sm font-semibold tabular-nums text-grey-foreground/70 dark:text-muted-foreground">
								—
							</span>
						)}
						<CollapsibleTrigger asChild>
							<button
								type="button"
								aria-expanded={specsOpen}
								aria-label={specsOpen ? "Hide plan details" : "Show plan details"}
								onClick={(e) => e.stopPropagation()}
								className={cn(
									"flex size-6 shrink-0 items-center justify-center rounded-full p-0 leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-1",
									visiblySelected
										? "text-secondary hover:bg-secondary/10"
										: "text-grey-foreground hover:bg-black/[0.04] dark:text-muted-foreground dark:hover:bg-white/5",
								)}
							>
								{specsOpen ? (
									<Minus className="block size-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
								) : (
									<Plus className="block size-3.5 shrink-0" strokeWidth={2.25} aria-hidden />
								)}
							</button>
						</CollapsibleTrigger>
					</div>
				</div>
				<CollapsibleContent className="min-w-0 w-full overflow-hidden data-[state=open]:border-t data-[state=open]:border-grey/15 data-[state=open]:dark:border-border">
					<div
						className={cn(
							"px-[15px] pb-3 pt-2 text-left sm:px-5",
							visiblySelected ? "bg-white dark:bg-card" : "bg-[#e8edf1] dark:bg-[#060d12]",
						)}
					>
						<span className="text-sm font-medium text-secondary">This includes:</span>
						{plan.featureGroups?.length ? (
							plan.featureGroups.map((group) => (
								<div key={group.title} className="mt-2">
									<p className="text-sm font-semibold text-foreground dark:text-foreground">{group.title}</p>
									<div className="mt-2 space-y-1 text-sm text-grey-foreground dark:text-muted-foreground">
										{group.features.map((f, i) => (
											<PackageFeatureItem
												key={f._id ?? `${group.title}-${i}`}
												feature={hostingPlanFeatureToPackageFeature(f, i, group.title)}
											/>
										))}
									</div>
								</div>
							))
						) : (
							<p className="mt-2 text-xxsm text-muted-foreground">No specifications listed for this plan yet.</p>
						)}
					</div>
				</CollapsibleContent>
			</Collapsible>
			{footerNotice ? (
				<p className="border-t border-grey/15 px-[15px] pb-2.5 pt-1.5 text-center text-xxsm font-normal leading-snug text-grey-foreground dark:border-border dark:text-muted-foreground sm:px-5">
					{footerNotice}
				</p>
			) : null}
		</div>
	);
}

type Props = {
	service: Service;
	session: Session | null;
	/** Defaults to `add-to-basket`; use a unique id when multiple forms mount on one page. */
	addToBasketButtonId?: string;
	onSelectedPackageChange?: (packageId: string) => void;
	/** Preferred design variant picked on a multi-design landing page (saved on the order). */
	preferredDesignServiceId?: string;
	/** True while the chosen design's own service record is still loading. */
	designPurchasePending?: boolean;
};

export function attributesToFeatures(attributes?: Record<string, string>): PackageFeature[] {
	if (attributes === undefined) return [];

	const normalizeDescription = (input: unknown): string => {
		if (input === null || input === undefined) return "N/A";
		const text = input.toString().trim();
		return text.length > 0 ? text : "N/A";
	};

	return Object.entries(attributes).map(([key, rawValue], index) => {
		const value = (rawValue ?? "").toString().trim();

		try {
			const parsed = JSON.parse(value) as { status?: FeatureStatus; description?: unknown };
			if (typeof parsed === "object" && parsed !== null && typeof parsed.status === "string") {
				return {
					id: `${key}-${index}`,
					name: key,
					status: parsed.status ?? FeatureStatus.NONE,
					description: normalizeDescription(parsed.description),
				};
			}
		} catch (error) {
			// fallback to heuristic below
		}

		const lower = value.toLowerCase();

		let status = FeatureStatus.NONE;
		if (POSITIVE_VALUES.has(lower)) status = FeatureStatus.RIGHT;
		else if (NEGATIVE_VALUES.has(lower)) status = FeatureStatus.WRONG;

		const hasExplicitDescription = value.length > 0 && !POSITIVE_VALUES.has(lower) && !NEGATIVE_VALUES.has(lower);
		const description = hasExplicitDescription ? value : value.length === 0 ? "N/A" : undefined;

		return {
			id: `${key}-${index}`,
			name: key,
			status,
			description,
		};
	});
}

export default function PackageSection({
	service,
	session,
	addToBasketButtonId = "add-to-basket",
	onSelectedPackageChange,
	preferredDesignServiceId,
	designPurchasePending = false,
}: Props) {
	const [isInPremium, setIsInPremium] = useState(false);
	const [selectedPremiumPlan, setPremiumPlanStatus] = useState(false);

	useEffect(() => {
		const planId = session?.user?.plan?.planID;
		const duration = session?.user?.plan?.duration;
		if (session === null || planId === undefined || planId === "") {
			setIsInPremium(false);
			return;
		}
		let cancelled = false;
		void isPremiumSubscriptionPlanMember(planId, duration).then((ok) => {
			if (!cancelled) setIsInPremium(ok);
		});
		return () => {
			cancelled = true;
		};
	}, [session?.user?.id, session?.user?.plan?.planID, session?.user?.plan?.duration]);

	useEffect(() => {
		setPremiumPlanStatus(isInPremium);
	}, [isInPremium]);

	const storefrontPackages = useMemo(
		() => storefrontPackagesForService(service),
		[service],
	);
	const defaultPackageId = useMemo(
		() => getDefaultSelectedPackage(storefrontPackages)?._id ?? "",
		[storefrontPackages],
	);
	const [selectedPackageId, setSelectedPackageId] = useState(defaultPackageId);
	const [selectedAddons, setSelectedAddons] = useState<SelectedAddon[]>([]);
	const [selectedPaymentOption, setSelectedPaymentOption] = useState<"full" | "installments" | "trial">("installments");
	const [hostingPillPlans, setHostingPillPlans] = useState<Plan[]>([]);
	const [selectedHostingPlanId, setSelectedHostingPlanId] = useState<string | null>(null);
	const [hostingBillingCycle, setHostingBillingCycle] = useState<HostingBillingCycle>("monthly");
	const [showSignupModal, setShowSignupModal] = useState(false);
	const [signupReturnUrl, setSignupReturnUrl] = useState<string | undefined>(undefined);
	const [appliedPromos, setAppliedPromos] = useState<AppliedPromoCode[]>([]);
	const autoServiceCheckoutStartedRef = useRef(false);
	const serviceCheckoutStorageKey = `${SERVICE_CHECKOUT_STORAGE_PREFIX}:${service._id}`;

	const currency = useCurrency();

	useEffect(() => {
		let cancelled = false;
		void getPlans({ planType: "hosting", isCustomPlan: false, status: PlanStatus.PUBLISHED }).then(
			(plans) => {
				if (cancelled) return;
				const pills = pickHostingPlansForServicePills(plans);
				setHostingPillPlans(pills);
			},
		);
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (hostingBillingCycle !== "yearly") return;
		const anyYearly = hostingPillPlans.some(
			(p) => getHostingPlanPriceForDuration(p, currency, UserPlan.yearly) !== undefined,
		);
		if (!anyYearly) setHostingBillingCycle("monthly");
	}, [hostingBillingCycle, hostingPillPlans, currency]);

	const selectedPackageTitleRef = useRef("");
	useEffect(() => {
		const current = storefrontPackages.find((pkg) => pkg._id === selectedPackageId);
		if (current?.title) selectedPackageTitleRef.current = current.title;
	}, [selectedPackageId, storefrontPackages]);

	useEffect(() => {
		if (storefrontPackages.length === 0) return;
		if (storefrontPackages.some((pkg) => pkg._id === selectedPackageId)) return;
		const wanted = selectedPackageTitleRef.current.trim().toLowerCase();
		const match = wanted
			? storefrontPackages.find((pkg) => pkg.title.trim().toLowerCase() === wanted)
			: undefined;
		const nextId = match?._id ?? defaultPackageId;
		if (nextId && nextId !== selectedPackageId) setSelectedPackageId(nextId);
	}, [defaultPackageId, selectedPackageId, storefrontPackages]);

	useEffect(() => {
		if (selectedPackageId.length === 0) return;
		onSelectedPackageChange?.(selectedPackageId);
	}, [onSelectedPackageChange, selectedPackageId]);

	const router = useRouter();
	const { toast } = useToast();
	const dispatch = useDispatch();
	const [createOrder, { isLoading: isCreatingOrder }] = useCreateOrderMutation();
	const [createGuestOrder] = useCreateGuestOrderMutation();
	const { data: hostingUsageData } = useHostingUsage(Boolean(session?.idToken));
	const { data: categoriesData } = useGetCategoriesQuery();
	const mainCategoryHostingEnabled = useMemo(
		() => buildMainCategoryHostingEnabledMap(categoriesData?.docs ?? []),
		[categoriesData?.docs],
	);

    const selectedPackage = useMemo(
        () => storefrontPackages.find((pkg) => pkg._id === selectedPackageId) ?? storefrontPackages[0],
        [selectedPackageId, storefrontPackages],
    );

	const activePackage = selectedPackage ?? storefrontPackages[0];

	const categoryShowsHosting = useMemo(
		() => serviceCategoryShowsHosting(service, mainCategoryHostingEnabled),
		[service, mainCategoryHostingEnabled],
	);
	const purchasedHostingEligibleCount =
		hostingUsageData?.purchasedHostingEligibleServiceCount ?? 0;
	const totalHostingEligibleCountForThisService = purchasedHostingEligibleCount + 1;
	const userActiveHostingTier: HostingPlanTier | null =
		hostingUsageData?.activeHostingTier ?? null;
	const hostingExceedsProMax =
		categoryShowsHosting &&
		hostingExceedsProPlanMax(totalHostingEligibleCountForThisService);
	const hostingCoveredByExistingPlan =
		categoryShowsHosting &&
		hostingTierCoversCount(userActiveHostingTier, totalHostingEligibleCountForThisService);
	const serviceNeedsHosting =
		categoryShowsHosting && !hostingCoveredByExistingPlan && !hostingExceedsProMax;

	const selectedHostingPlan = useMemo(() => {
		if (!selectedHostingPlanId) return null;
		return hostingPillPlans.find((p) => p._id === selectedHostingPlanId) ?? null;
	}, [selectedHostingPlanId, hostingPillPlans]);

	const isExistingHostingUpgrade = useMemo(
		() => isExistingHostingTierUpgrade(userActiveHostingTier, selectedHostingPlan),
		[userActiveHostingTier, selectedHostingPlan],
	);

	useEffect(() => {
		if (!serviceNeedsHosting || hostingPillPlans.length === 0) {
			setSelectedHostingPlanId(null);
			return;
		}
		setSelectedHostingPlanId((prev) => {
			if (
				prev !== null &&
				hostingPillPlans.some(
					(p) => p._id === prev && !isHostingPlanBlockedForUsage(p, totalHostingEligibleCountForThisService),
				)
			) {
				return prev;
			}
			return pickPreferredHostingPlanForUsage(
				hostingPillPlans,
				totalHostingEligibleCountForThisService,
			);
		});
	}, [serviceNeedsHosting, hostingPillPlans, totalHostingEligibleCountForThisService]);

	const deliveryLabel = useMemo(() => {
		const raw = Number(activePackage?.dueDays);
		if (!Number.isFinite(raw) || raw <= 0) return null;
		const days = Math.round(raw);
		return `${days}-day delivery`;
	}, [activePackage?.dueDays]);

	const packageFeatures = useMemo(
		() => attributesToFeatures(activePackage?.attributes),
		[activePackage],
	);

	const serviceAddons = service.addons ?? [];

	const expressAddon = useMemo(
		() => serviceAddons.find((addon) => isExpressDeliveryToggleAddon(addon)),
		[serviceAddons],
	);

	const expressDeliveryDays = useMemo(() => {
		const raw = Number(activePackage?.dueDays);
		if (!Number.isFinite(raw) || raw <= 0) return null;
		return expressDeliveryDaysFromPackageDueDays(raw);
	}, [activePackage?.dueDays]);

	const additionalAddons = useMemo(
		() => serviceAddons.filter((addon) => addon._id !== expressAddon?._id),
		[serviceAddons, expressAddon],
	);

	const selectedAddonEntries: [string, BasketAddonItem][] = useMemo(
		() => selectedAddons.map(({ id, ...rest }) => [id, { ...rest }]),
		[selectedAddons],
	);

	const handleAddonChange = useCallback(
		(addonId: string, type: AddonInputType, value: SelectedAddon["value"]) => {
			setSelectedAddons((prev) => {
				const next = prev.slice();
				const existingIndex = next.findIndex((x) => x.id === addonId);

				if (
					(type === AddonInputType.toggle && value === false) ||
					(type === AddonInputType.number && (value === 0 || Number.isNaN(value as number)))
				) {
					if (existingIndex !== -1) next.splice(existingIndex, 1);
					return next;
				}

				const payload: SelectedAddon = { id: addonId, type, value };
				if (existingIndex !== -1) next[existingIndex] = payload;
				else next.push(payload);

				return next;
			});
		},
		[],
	);

    // Use the state-based currency (single source of truth)
    // This prevents hydration mismatches and ensures consistent currency throughout the component
    const { addonTotal, packageTotal } = useMemo(() => {
        if (activePackage === undefined) {
            return { addonTotal: 0, packageTotal: 0 };
        }

		return calculatePackageTotal(activePackage, serviceAddons, selectedAddonEntries, currency);
    }, [activePackage, serviceAddons, selectedAddonEntries, currency]);

	const templateLicenseAmount = useMemo(() => {
		const amounts = service.envatoTemplateLicenseAmounts;
		if (!amounts || typeof amounts !== "object") return undefined;
		const direct = amounts[currency];
		if (typeof direct === "number" && Number.isFinite(direct) && direct > 0) {
			return direct;
		}
		return undefined;
	}, [service.envatoTemplateLicenseAmounts, currency]);

	const hostingPriceInSummary = useMemo(() => {
		if (!serviceNeedsHosting) return 0;
		if (isExistingHostingUpgrade) return 0;
		if (hostingPillPlans.length === 0) return 0;
		const plan =
			hostingPillPlans.find((p) => p._id === selectedHostingPlanId) ?? hostingPillPlans[0];
		const duration = hostingBillingCycle === "yearly" ? UserPlan.yearly : UserPlan.monthly;
		const raw = getHostingPlanPriceForDuration(plan, currency, duration);
		return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
	}, [serviceNeedsHosting, isExistingHostingUpgrade, hostingPillPlans, selectedHostingPlanId, hostingBillingCycle, currency]);

	const templateLicenseLineAmount =
		templateLicenseAmount !== undefined && templateLicenseAmount > 0 ? templateLicenseAmount : 0;

	/** Monthly hosting for the headline price breakdown (always monthly, even if yearly is selected below). */
	const headlineHostingMonthlyAmount = useMemo(() => {
		if (!serviceNeedsHosting || isExistingHostingUpgrade) return 0;
		if (hostingPillPlans.length === 0) return 0;
		const plan =
			hostingPillPlans.find((p) => p._id === selectedHostingPlanId) ?? hostingPillPlans[0];
		const raw = getHostingPlanPriceForDuration(plan, currency, UserPlan.monthly);
		return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : 0;
	}, [
		serviceNeedsHosting,
		isExistingHostingUpgrade,
		hostingPillPlans,
		selectedHostingPlanId,
		currency,
	]);

	/** Package list price + Envato license (when shown) + selected hosting + add-ons; discounts apply downstream. */
	const totalAmount =
		packageTotal + templateLicenseLineAmount + hostingPriceInSummary + addonTotal;
	const countdownStartsAtMs = service.countdownOffer?.startsAt
		? new Date(service.countdownOffer.startsAt).getTime()
		: null;
	const countdownEndsAtMs = service.countdownOffer?.endsAt
		? new Date(service.countdownOffer.endsAt).getTime()
		: null;
	const countdownDurationSeconds = useMemo(() => {
		const fromField =
			typeof service.countdownOffer?.durationSeconds === "number" &&
			Number.isFinite(service.countdownOffer.durationSeconds)
				? Math.floor(service.countdownOffer.durationSeconds)
				: 0;
		if (fromField > 0) return fromField;
		if (
			countdownStartsAtMs !== null &&
			countdownEndsAtMs !== null &&
			!Number.isNaN(countdownStartsAtMs) &&
			!Number.isNaN(countdownEndsAtMs) &&
			countdownEndsAtMs > countdownStartsAtMs
		) {
			return Math.max(1, Math.floor((countdownEndsAtMs - countdownStartsAtMs) / 1000));
		}
		return 0;
	}, [countdownEndsAtMs, countdownStartsAtMs, service.countdownOffer?.durationSeconds]);

	const [, setOfferClock] = useState(0);
	useEffect(() => {
		if (service.countdownOffer?.enabled !== true) return;
		const id = window.setInterval(() => setOfferClock((n) => n + 1), 250);
		return () => window.clearInterval(id);
	}, [service.countdownOffer?.enabled]);

	const [clientDeadlineMs, setClientDeadlineMs] = useState<number | null>(null);
	useLayoutEffect(() => {
		if (typeof window === "undefined") return;
		setClientDeadlineMs(
			resolveClientDeadlineMs(
				service,
				countdownStartsAtMs,
				countdownEndsAtMs,
				countdownDurationSeconds,
				Date.now(),
			),
		);
	}, [
		countdownDurationSeconds,
		countdownEndsAtMs,
		countdownStartsAtMs,
		service,
		service._id,
		service.countdownOffer?.enabled,
	]);

	const nowMs = Date.now();
	const withinClientCountdown =
		clientDeadlineMs === null || nowMs < clientDeadlineMs;
	const countdownElapsed =
		clientDeadlineMs !== null && nowMs >= clientDeadlineMs;
	const countdownEndedGraceExpired =
		clientDeadlineMs !== null &&
		nowMs >= clientDeadlineMs + SERVICE_COUNTDOWN_ENDED_GRACE_MS;
	const isInOfferCampaignWindow =
		service.countdownOffer?.enabled === true &&
		countdownStartsAtMs !== null &&
		countdownEndsAtMs !== null &&
		!Number.isNaN(countdownStartsAtMs) &&
		!Number.isNaN(countdownEndsAtMs) &&
		nowMs >= countdownStartsAtMs &&
		nowMs < countdownEndsAtMs &&
		!countdownEndedGraceExpired;
	const configuredDiscountPercent = service.countdownOffer?.discountPercent;
	const isLimitedOfferDiscountActive =
		isInOfferCampaignWindow &&
		withinClientCountdown &&
		typeof configuredDiscountPercent === "number" &&
		Number.isFinite(configuredDiscountPercent) &&
		configuredDiscountPercent > 0;
	const limitedOfferDiscountPercent = isLimitedOfferDiscountActive
		? Math.min(100, Math.max(0, Math.round(configuredDiscountPercent)))
		: undefined;
	const limitedOfferShowEndedBadge =
		isInOfferCampaignWindow && countdownElapsed;

	const PAY_IN_FULL_DISCOUNT = 0.1;
	/** Package line includes selected add-ons, so package-level discounts follow that displayed amount. */
	const packageDiscountBase = Math.round((packageTotal + addonTotal) * 100) / 100;
	const nonPackageLineTotal = templateLicenseLineAmount + hostingPriceInSummary;
	const packageAfterMember = selectedPremiumPlan
		? Math.round(packageDiscountBase * (1 - Discount / 100) * 100) / 100
		: packageDiscountBase;
	const limitedOfferMultiplier =
		limitedOfferDiscountPercent !== undefined && limitedOfferDiscountPercent > 0
			? 1 - limitedOfferDiscountPercent / 100
			: 1;
	const packageAfterLimited =
		limitedOfferDiscountPercent !== undefined && limitedOfferDiscountPercent > 0
			? Math.round(packageAfterMember * limitedOfferMultiplier * 100) / 100
			: packageAfterMember;
	const packageAfterPayInFull =
		Math.round(packageAfterLimited * (1 - PAY_IN_FULL_DISCOUNT) * 100) / 100;
	const selectedPackageDiscountAmount =
		selectedPaymentOption === "full"
			? Math.round(Math.max(0, packageDiscountBase - packageAfterPayInFull) * 100) / 100
			: Math.round(Math.max(0, packageDiscountBase - packageAfterLimited) * 100) / 100;
	const effectiveTotal =
		Math.round((packageAfterLimited + nonPackageLineTotal) * 100) / 100;
	const totalBeforeLimitedOfferRow =
		Math.round((packageAfterMember + nonPackageLineTotal) * 100) / 100;
	/**
	 * Derived from the same rounded-up totals shown in the summary, so the discount row,
	 * the offer badge and the struck-through total always reconcile (all prices display via ceil).
	 */
	const limitedOfferPackageDiscountAmount =
		limitedOfferDiscountPercent !== undefined && limitedOfferDiscountPercent > 0
			? Math.max(0, Math.ceil(totalBeforeLimitedOfferRow) - Math.ceil(effectiveTotal))
			: 0;

	const packagePriceBeforePromo =
		selectedPaymentOption === "full" ? packageAfterPayInFull : packageAfterLimited;
	const promoOffOnPackage = appliedPromos.reduce((sum, promo) => {
		const applies =
			!promo.applyTo?.length || promo.applyTo.includes("PACKAGE");
		if (!applies) return sum;
		return sum + Math.max(0, promo.discountAmount);
	}, 0);
	const promoOffOnPackageCapped = Math.min(
		promoOffOnPackage,
		Math.max(0, packagePriceBeforePromo),
	);
	const packagePriceAfterPromo =
		Math.round(Math.max(0, packagePriceBeforePromo - promoOffOnPackageCapped) * 100) / 100;

	/** Headline (package + license + monthly hosting); same discount stack as checkout on the package line. */
	const headlineNonPackageAmount = templateLicenseLineAmount + headlineHostingMonthlyAmount;
	const headlinePackageList = packageTotal;
	const headlinePackageAfterMember = selectedPremiumPlan
		? Math.round(headlinePackageList * (1 - Discount / 100) * 100) / 100
		: headlinePackageList;
	const headlinePackageAfterLimited =
		limitedOfferDiscountPercent !== undefined && limitedOfferDiscountPercent > 0
			? Math.round(headlinePackageAfterMember * limitedOfferMultiplier * 100) / 100
			: headlinePackageAfterMember;
	const headlinePackageFinal =
		selectedPaymentOption === "full"
			? Math.round(headlinePackageAfterLimited * (1 - PAY_IN_FULL_DISCOUNT) * 100) / 100
			: headlinePackageAfterLimited;
	/** Attribute package promo onto the headline package line (excludes add-ons). */
	const promoOffOnHeadlinePackage =
		packagePriceBeforePromo > 0.001
			? Math.round(
					((promoOffOnPackageCapped * headlinePackageFinal) / packagePriceBeforePromo) * 100,
				) / 100
			: 0;
	const headlinePackageFinalAfterPromo =
		Math.round(Math.max(0, headlinePackageFinal - promoOffOnHeadlinePackage) * 100) / 100;
	const headlinePriceBeforeDiscount =
		Math.round((headlinePackageList + headlineNonPackageAmount) * 100) / 100;
	const headlinePriceTotal =
		Math.round((headlinePackageFinalAfterPromo + headlineNonPackageAmount) * 100) / 100;
	const showHeadlineDiscountedPrice =
		Math.ceil(headlinePriceBeforeDiscount) > Math.ceil(headlinePriceTotal);
	const headlineDiscountSavings = showHeadlineDiscountedPrice
		? Math.max(0, Math.ceil(headlinePriceBeforeDiscount) - Math.ceil(headlinePriceTotal))
		: 0;

	const installmentCount = effectiveTotal >= INSTALLMENT_THRESHOLD ? 3 : 2;

	const payInFullAmountBase =
		Math.round((packageAfterPayInFull + nonPackageLineTotal) * 100) / 100;
	const payInFullSavings = effectiveTotal - payInFullAmountBase;

	const promoDiscountAmount = appliedPromos.length
		? Math.min(
				appliedPromos.reduce((s, p) => s + Math.max(0, p.discountAmount), 0),
				selectedPaymentOption === "full" ? payInFullAmountBase : effectiveTotal,
			)
		: 0;
	const payInFullAmount =
		Math.round(Math.max(0, payInFullAmountBase - promoDiscountAmount) * 100) / 100;
	const effectiveTotalAfterPromo =
		Math.round(Math.max(0, effectiveTotal - promoDiscountAmount) * 100) / 100;

	/** Due today: license + hosting + 10% of the package line (package + add-ons), capped to cart total. */
	const packageAdvanceTenPercent =
		Math.round(Math.max(0, packageAfterLimited) * 0.1 * 100) / 100;
	const firstInstallmentAmountBase = Math.min(
		Math.round((Math.max(nonPackageLineTotal, 0) + packageAdvanceTenPercent) * 100) / 100,
		effectiveTotal,
	);
	const { dueToday: firstInstallmentAmount, payLater: remainingAmount } =
		applyPromosToInstallments(
			firstInstallmentAmountBase,
			effectiveTotal,
			appliedPromos,
		);
	const remainingInstallmentAmount =
		installmentCount > 1 ? remainingAmount / (installmentCount - 1) : remainingAmount;
	const installmentAmount = remainingInstallmentAmount; // For display purposes (remaining installments)
	const payNowAmount =
		selectedPaymentOption === "full"
			? payInFullAmount
			: selectedPaymentOption === "trial"
				? 0
				: firstInstallmentAmount;

	const packageTierForPromo = resolvePackageTierFromTitle(activePackage?.title);

	const trialAvailable =
		isInOfferCampaignWindow &&
		withinClientCountdown &&
		service.countdownOffer?.showFreeTrial === true;

	useEffect(() => {
		if (!trialAvailable) {
			setSelectedPaymentOption((prev) => (prev === "trial" ? "installments" : prev));
			return;
		}
		setSelectedPaymentOption("trial");
	}, [trialAvailable]);

	const eligibleForPremiumMemberDiscount = Boolean(session?.user) && isInPremium;
	const premiumMemberPackageDiscountAmount = useMemo(() => {
		if (!eligibleForPremiumMemberDiscount) return 0;
		return Math.round(Math.max(0, packageDiscountBase) * (Discount / 100) * 100) / 100;
	}, [eligibleForPremiumMemberDiscount, packageDiscountBase]);

	useEffect(() => {
		if (typeof window === "undefined" || activePackage === undefined) return;
		const basketLike: BasketItem = {
			serviceId: service._id,
			packageId: activePackage._id,
			quantity: 1,
			addons: Object.fromEntries(selectedAddonEntries),
		};
		const built = buildCommerceItemFromBasketLine(service, basketLike, currency);
		if (!built) return;
		ga4ViewItem({
			currency,
			value: effectiveTotal,
			items: [{ ...built.item, price: effectiveTotal }],
		});
	}, [service._id, service.title, activePackage?._id, currency, effectiveTotal, selectedAddonEntries]);

	const currentPageReturnUrl = () =>
		typeof window !== "undefined"
			? `${window.location.pathname}${window.location.search}${window.location.hash}`
			: undefined;

	const serviceCheckoutReturnUrl = () => {
		if (typeof window === "undefined") return undefined;
		const url = new URL(window.location.href);
		url.searchParams.set(SERVICE_CHECKOUT_RETURN_PARAM, "1");
		return `${url.pathname}${url.search}${url.hash}`;
	};

	const persistPendingServiceCheckout = useCallback(() => {
		if (typeof window === "undefined" || !activePackage) return;
		const pending: PendingServicePageCheckout = {
			serviceId: service._id,
			packageId: activePackage._id,
			addons: selectedAddons,
			selectedPaymentOption,
			selectedPremiumPlan,
			hostingPlanId: selectedHostingPlanId,
			hostingBillingCycle,
			createdAt: Date.now(),
		};
		window.sessionStorage.setItem(serviceCheckoutStorageKey, JSON.stringify(pending));
	}, [
		activePackage,
		hostingBillingCycle,
		selectedAddons,
		selectedHostingPlanId,
		selectedPaymentOption,
		selectedPremiumPlan,
		service._id,
		serviceCheckoutStorageKey,
	]);

	const readPendingServiceCheckout = useCallback((): PendingServicePageCheckout | null => {
		if (typeof window === "undefined") return null;
		const raw = window.sessionStorage.getItem(serviceCheckoutStorageKey);
		if (!raw) return null;
		try {
			const pending = JSON.parse(raw) as PendingServicePageCheckout;
			if (
				pending.serviceId !== service._id ||
				typeof pending.packageId !== "string" ||
				!Array.isArray(pending.addons) ||
				Date.now() - pending.createdAt > SERVICE_CHECKOUT_MAX_AGE_MS
			) {
				window.sessionStorage.removeItem(serviceCheckoutStorageKey);
				return null;
			}
			return pending;
		} catch {
			window.sessionStorage.removeItem(serviceCheckoutStorageKey);
			return null;
		}
	}, [service._id, serviceCheckoutStorageKey]);

	const startServicePageCheckout = useCallback(async () => {
		if (designPurchasePending) {
			toast({ title: "Still loading that design", description: "Try again in a moment." });
			return;
		}
		if (!activePackage) {
			toast({
				title: "No package available",
				description: "Please select a package before continuing.",
			});
			return;
		}

		invalidateApiSessionCache();
		const liveSession = await getSession();
		const isGuest = !liveSession?.idToken?.trim();

		if (selectedPremiumPlan && !isInPremium) {
			if (isGuest) {
				persistPendingServiceCheckout();
				setSignupReturnUrl(serviceCheckoutReturnUrl() ?? currentPageReturnUrl());
				setShowSignupModal(true);
				toast({ title: "Sign in required for member discount" });
				return;
			}
			toast({ title: "Please select a premium plan" });
			router.push("/subscription/monthly");
			return;
		}

		const addons = selectedAddonEntries
			.map(([addonId, v]) => serializeAddonForOrderApi(addonId, v.type, v.value))
			.filter((row): row is NonNullable<typeof row> => row != null);
		const theme: "light" | "dark" =
			typeof document !== "undefined" && document.documentElement.classList.contains("dark")
				? "dark"
				: "light";
		if (
			serviceNeedsHosting &&
			(hostingPillPlans.length === 0 || selectedHostingPlanId === null)
		) {
			toast({
				title: "Hosting not ready",
				description: "Please wait for hosting options to load, then try again.",
				variant: "destructive",
			});
			return;
		}
		if (hostingExceedsProMax) {
			toast({
				title: "Custom hosting required",
				description: HOSTING_CUSTOM_PLAN_SUPPORT_MESSAGE,
				variant: "destructive",
			});
			return;
		}

		const orderPayload = {
			items: [
				{
					serviceId: service._id,
					...(preferredDesignServiceId?.trim()
						? { preferredDesignServiceId: preferredDesignServiceId.trim() }
						: {}),
					package: { packageId: activePackage._id, quantity: 1 },
					addons,
				},
			],
			paymentOption: selectedPaymentOption,
			installmentCount: selectedPaymentOption === "trial" ? 1 : installmentCount,
			...(selectedPaymentOption === "installments" && {
				firstInstallmentAmount: firstInstallmentAmount,
			}),
			currency,
			theme,
			servicePageCheckout: {
				applyMemberDiscount: isGuest ? false : selectedPremiumPlan,
				...(serviceNeedsHosting && {
					hostingPlanId: selectedHostingPlanId!,
					hostingBillingCycle,
				}),
			},
			...(appliedPromos.length
				? {
						promoCode: appliedPromos[0].code,
						promoCodes: appliedPromos.map((p) => p.code),
					}
				: {}),
		};

		try {
			const { stripeUrl } = isGuest
				? await createGuestOrder(orderPayload).unwrap()
				: await createOrder(orderPayload).unwrap();
			if (!isSafeStripeUrl(stripeUrl)) {
				toast({ title: "Invalid payment URL", variant: "destructive" });
				return;
			}
			const basketLike: BasketItem = {
				serviceId: service._id,
				packageId: activePackage._id,
				quantity: 1,
				addons: Object.fromEntries(selectedAddonEntries),
			};
			const built = buildCommerceItemFromBasketLine(service, basketLike, currency);
			const checkoutItem = built
				? { ...built.item, price: payNowAmount }
				: {
						item_id: service._id,
						item_name: service.title,
						quantity: 1,
						price: payNowAmount,
					};
			goToStripeAfterCheckout(stripeUrl, {
				currency,
				value: payNowAmount,
				payment_type:
					selectedPaymentOption === "installments"
						? "installments"
						: selectedPaymentOption === "trial"
							? "trial"
							: "card",
				items: [checkoutItem],
			});
		} catch (err: unknown) {
			const error = err as {
				status?: number;
				tokenExpired?: boolean;
				data?: { message?: string; statusCode?: number };
			};
			if (
				!isGuest &&
				(error?.status === 401 ||
					error?.data?.statusCode === 401 ||
					error?.tokenExpired)
			) {
				invalidateApiSessionCache();
				persistPendingServiceCheckout();
				setSignupReturnUrl(serviceCheckoutReturnUrl() ?? currentPageReturnUrl());
				setShowSignupModal(true);
				return;
			}
			const msg =
				typeof error?.data?.message === "string"
					? error.data.message
					: "Failed to start checkout.";
			toast({ title: "Checkout error", description: msg, variant: "destructive" });
		}
	}, [
		activePackage,
		createGuestOrder,
		createOrder,
		currency,
		designPurchasePending,
		firstInstallmentAmount,
		hostingBillingCycle,
		hostingExceedsProMax,
		hostingPillPlans.length,
		installmentCount,
		isInPremium,
		payNowAmount,
		persistPendingServiceCheckout,
		router,
		selectedAddonEntries,
		selectedHostingPlanId,
		selectedPaymentOption,
		selectedPremiumPlan,
		service,
		serviceNeedsHosting,
		preferredDesignServiceId,
		toast,
		appliedPromos,
	]);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const params = new URLSearchParams(window.location.search);
		if (params.get(SERVICE_CHECKOUT_RETURN_PARAM) !== "1") return;

		const pending = readPendingServiceCheckout();
		if (!pending) return;

		if (pending.packageId !== selectedPackageId) setSelectedPackageId(pending.packageId);
		setSelectedAddons(pending.addons);
		if (pending.selectedPaymentOption !== selectedPaymentOption) {
			setSelectedPaymentOption(pending.selectedPaymentOption);
		}
		if (pending.selectedPremiumPlan !== selectedPremiumPlan) {
			setPremiumPlanStatus(pending.selectedPremiumPlan);
		}
		if (pending.hostingBillingCycle !== hostingBillingCycle) {
			setHostingBillingCycle(pending.hostingBillingCycle);
		}
		if ((pending.hostingPlanId !== null || !serviceNeedsHosting) && pending.hostingPlanId !== selectedHostingPlanId) {
			setSelectedHostingPlanId(pending.hostingPlanId);
		}
	}, [
		hostingBillingCycle,
		readPendingServiceCheckout,
		selectedHostingPlanId,
		selectedPackageId,
		selectedPaymentOption,
		selectedPremiumPlan,
		serviceNeedsHosting,
	]);

	useEffect(() => {
		if (typeof window === "undefined" || autoServiceCheckoutStartedRef.current) return;
		const url = new URL(window.location.href);
		if (url.searchParams.get(SERVICE_CHECKOUT_RETURN_PARAM) !== "1") return;

		const pending = readPendingServiceCheckout();
		if (!pending) return;
		if (pending.packageId !== selectedPackageId) return;
		if (pending.selectedPaymentOption !== selectedPaymentOption) return;
		if (pending.selectedPremiumPlan !== selectedPremiumPlan) return;
		if (pending.hostingBillingCycle !== hostingBillingCycle) return;
		if (pending.hostingPlanId !== null && pending.hostingPlanId !== selectedHostingPlanId) return;
		if (hostingExceedsProMax) return;
		if (serviceNeedsHosting && (hostingPillPlans.length === 0 || selectedHostingPlanId === null)) return;

		autoServiceCheckoutStartedRef.current = true;
		let cancelled = false;
		void (async () => {
			const liveSession = await getSession();
			if (cancelled) return;
			if (!liveSession?.idToken?.trim()) {
				autoServiceCheckoutStartedRef.current = false;
				return;
			}
			window.sessionStorage.removeItem(serviceCheckoutStorageKey);
			url.searchParams.delete(SERVICE_CHECKOUT_RETURN_PARAM);
			window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
			await startServicePageCheckout();
		})();

		return () => {
			cancelled = true;
		};
	}, [
		hostingBillingCycle,
		hostingPillPlans.length,
		readPendingServiceCheckout,
		selectedHostingPlanId,
		selectedPackageId,
		selectedPaymentOption,
		selectedPremiumPlan,
		serviceCheckoutStorageKey,
		serviceNeedsHosting,
		session?.user?.id,
		startServicePageCheckout,
	]);

	const packageCount = storefrontPackages.length;
	const columnClassByCount: Record<number, string> = {
		1: "grid-cols-1",
		2: "grid-cols-2",
		3: "grid-cols-3",
		4: "grid-cols-4",
	};
	const tabColumnClass = columnClassByCount[packageCount] ?? "grid-cols-3";

	const onFormSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
		e.preventDefault();

		const { submitter } = e.nativeEvent as SubmitEvent;
		if (submitter === null) return;

		if (designPurchasePending) {
			toast({ title: "Still loading that design", description: "Try again in a moment." });
			return;
		}

		if (activePackage === undefined) {
			return toast({
				title: "No package available",
				description: "Please select a package before continuing.",
			});
		}

		const action = addToBasket({
			serviceId: service._id,
			packageId: activePackage._id,
			quantity: 1,
			addons: Object.fromEntries(selectedAddonEntries),
			...(preferredDesignServiceId?.trim()
				? { preferredDesignServiceId: preferredDesignServiceId.trim() }
				: {}),
		});
		dispatch(action);

		if (submitter.id === "add-to-basket") {
			const basketLike: BasketItem = {
				serviceId: service._id,
				packageId: activePackage._id,
				quantity: 1,
				addons: Object.fromEntries(selectedAddonEntries),
				...(preferredDesignServiceId?.trim()
					? { preferredDesignServiceId: preferredDesignServiceId.trim() }
					: {}),
			};
			const built = buildCommerceItemFromBasketLine(service, basketLike, currency);
			if (built) {
				ga4AddToCart({
					currency,
					value: effectiveTotal,
					items: [{ ...built.item, price: effectiveTotal }],
				});
			}
			showAddedToBasketToast(service, activePackage.title);
			return;
		}

		if (submitter.id === "checkout") {
			await startServicePageCheckout();
		}
	};

	return (
		<>
		<form onSubmit={onFormSubmit} className="flex w-full flex-col lg:max-w-[26rem]">
			<div className="flex w-full flex-col overflow-hidden rounded-[24px] bg-background shadow-custom dark:bg-card">
		<div className={cn("grid h-[3.75rem] bg-background dark:bg-card", tabColumnClass)}>
					{storefrontPackages.map((pkg, index) => {
						const isActive = activePackage?._id === pkg._id;

						return (
							<button
								type="button"
								key={pkg._id}
								onClick={() => {
									if (selectedPackageId === pkg._id) return;
									ga4CtaClick({
										cta_id: `package_tab_${pkg.title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "unknown"}`,
										cta_name: pkg.title,
										cta_location: "service_package_tabs",
									});
									setSelectedPackageId(pkg._id);
								}}
						className={cn(
							"relative flex h-full items-center justify-center text-sm2 font-semibold transition-colors",
							isActive 
								? "bg-background dark:bg-card text-secondary" 
								: "bg-[#f6f7f8] dark:bg-[#080F14] text-[#C4C8CD] dark:text-muted-foreground",
						)}
							>
								<span className="relative z-10 text-center">{pkg.title}</span>
						{index < storefrontPackages.length - 1 &&
							activePackage?._id !== pkg._id &&
							activePackage?._id !== storefrontPackages[index + 1]?._id && (
								<span
									className="pointer-events-none absolute right-0 top-[10px] h-[calc(100%-20px)] w-px bg-[#e7eaed] dark:bg-border"
									aria-hidden="true"
								/>
							)}
							</button>
						);
					})}
				</div>

		<div className="flex flex-col gap-[0.65rem] p-[15px] sm:p-6">
				<div className="space-y-1.5">
					<div
						className={cn(
							"flex w-full min-w-0 items-center gap-2",
							showHeadlineDiscountedPrice && headlineDiscountSavings > 0
								? "justify-between"
								: "justify-start",
						)}
					>
						<div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
							{showHeadlineDiscountedPrice ? (
								<>
									<span className="text-sm font-semibold tabular-nums leading-tight text-secondary line-through decoration-secondary/80 sm:text-base">
										{formatCurrency(headlinePriceBeforeDiscount, currency)}
									</span>
									<span className="text-lg font-bold tabular-nums leading-tight text-[#ef3c4e] sm:text-xl">
										{formatCurrency(headlinePriceTotal, currency)}
									</span>
								</>
							) : (
								<span className="text-base font-semibold tabular-nums leading-tight text-secondary sm:text-lg">
									{formatCurrency(headlinePriceTotal, currency)}
								</span>
							)}
							<Dialog>
								<DialogTrigger asChild>
									<button
										type="button"
										className={pricingSummaryInfoBtnClass({ size: "sm" })}
										aria-label="Price details"
										{...getCtaDataAttrs({
											ctaId: "price_details_package_card",
											ctaName: "Price details",
											ctaLocation: "service_package_card",
										})}
									>
										<InfoIcon className="size-2.5" />
									</button>
								</DialogTrigger>
								<DialogContent
									className="w-[calc(100vw-2rem)] max-w-sm gap-0 overflow-y-auto p-4 sm:rounded-lg"
								>
									<DialogTitle className="pr-6 text-left text-sm font-semibold text-foreground">
										Price details
									</DialogTitle>
									<DialogDescription asChild>
										<div className="mt-3 text-left text-sm text-grey-foreground dark:text-muted-foreground">
											<div className="space-y-2">
												<div className="flex items-start justify-between gap-4">
													<span>Package (one-time)</span>
													{Math.ceil(headlinePackageList) >
													Math.ceil(headlinePackageFinalAfterPromo) ? (
														<span className="inline-flex shrink-0 items-baseline gap-1.5 tabular-nums">
															<span className="text-secondary line-through decoration-secondary/80">
																{formatCurrency(headlinePackageList, currency)}
															</span>
															<span className="font-semibold text-[#ef3c4e]">
																{formatCurrency(headlinePackageFinalAfterPromo, currency)}
															</span>
														</span>
													) : (
														<span className="shrink-0 tabular-nums text-foreground">
															{formatCurrency(headlinePackageFinalAfterPromo, currency)}
														</span>
													)}
												</div>
												{templateLicenseLineAmount > 0 ? (
													<div className="flex items-start justify-between gap-4">
														<span>License (one-time)</span>
														<span className="shrink-0 tabular-nums text-foreground">
															{formatCurrency(templateLicenseLineAmount, currency)}
														</span>
													</div>
												) : null}
												{headlineHostingMonthlyAmount > 0 ? (
													<div className="flex items-start justify-between gap-4">
														<span>Hosting (first month)</span>
														<span className="shrink-0 tabular-nums text-foreground">
															{formatCurrency(headlineHostingMonthlyAmount, currency)}
														</span>
													</div>
												) : null}
											</div>
											<div className="mt-3 flex items-start justify-between gap-4 border-t border-border pt-3 text-sm font-semibold text-foreground">
												<span>Total</span>
												{showHeadlineDiscountedPrice ? (
													<span className="inline-flex shrink-0 items-baseline gap-1.5 tabular-nums">
														<span className="font-normal text-secondary line-through decoration-secondary/80">
															{formatCurrency(headlinePriceBeforeDiscount, currency)}
														</span>
														<span className="font-bold text-[#ef3c4e]">
															{formatCurrency(headlinePriceTotal, currency)}
														</span>
													</span>
												) : (
													<span className="shrink-0 tabular-nums">
														{formatCurrency(headlinePriceTotal, currency)}
													</span>
												)}
											</div>
											{headlineHostingMonthlyAmount > 0 ? (
												<div className="mt-2 flex items-start justify-between gap-4 text-xs text-muted-foreground">
													<span>Hosting billed monthly</span>
													<span className="shrink-0 tabular-nums">
														{formatCurrency(headlineHostingMonthlyAmount, currency)} /m
													</span>
												</div>
											) : null}
										</div>
									</DialogDescription>
								</DialogContent>
							</Dialog>
						</div>
						{showHeadlineDiscountedPrice && headlineDiscountSavings > 0 ? (
							<span className="inline-flex shrink-0 -mr-[15px] items-center self-center whitespace-nowrap rounded-l-full rounded-r-none bg-[#ef3c4e] px-4 py-1.5 text-[10px] font-semibold uppercase leading-tight tracking-[0.06em] text-white shadow-sm sm:-mr-6 sm:text-[11px]">
								-{formatCurrency(headlineDiscountSavings, currency)} OFF
							</span>
						) : null}
					</div>
					<p className="whitespace-pre-wrap text-sm leading-6 text-grey-foreground/90 dark:text-muted-foreground">
						{activePackage?.description}
					</p>
				{deliveryLabel && (
					<div className="flex items-center justify-end">
						<div className="inline-flex items-center gap-2 text-sm font-medium text-grey-foreground/80 dark:text-muted-foreground">
							<Clock3 className="size-4 text-secondary" />
							<span className="text-sm text-grey-foreground/80 dark:text-muted-foreground">{deliveryLabel}</span>
						</div>
					</div>
				)}
			</div>

		<div className="mt-px">
			<Collapsible defaultOpen className="w-full min-w-0">
				<CollapsibleTrigger
					type="button"
					className="group flex w-full items-center justify-between gap-2 py-0.5 text-left"
				>
					<span className="text-sm font-medium text-secondary">This includes</span>
					<ChevronDown className="size-4 shrink-0 text-secondary transition-transform duration-200 group-data-[state=open]:rotate-180" />
				</CollapsibleTrigger>
				<CollapsibleContent className="overflow-hidden">
					<div className="pt-1">
						<PackageFeatureList features={packageFeatures} hideHeading />
					</div>
				</CollapsibleContent>
			</Collapsible>
		</div>

		{(additionalAddons.length > 0 || expressAddon) && (
			<Collapsible defaultOpen={false} className="mt-0.5 w-full min-w-0">
				<CollapsibleTrigger
					type="button"
					className="group flex w-full items-center justify-between gap-2 py-0.5 text-left"
				>
					<span className="text-sm font-medium text-secondary">Add-ons</span>
					<ChevronDown className="size-4 shrink-0 text-secondary transition-transform duration-200 group-data-[state=open]:rotate-180" />
				</CollapsibleTrigger>
				<CollapsibleContent className="overflow-hidden">
					<div className="pt-1">
						<AdditionalAddonList
							addons={additionalAddons}
							onChange={handleAddonChange}
							selectedAddons={selectedAddons}
							currency={currency}
						/>
						{expressAddon ? (
							<div className={additionalAddons.length > 0 ? "mt-3" : "mt-0.5"}>
								<ExpressDeliveryToggle
									expressAddon={expressAddon}
									selectedAddons={selectedAddons}
									onChange={handleAddonChange}
									expressDeliveryDays={expressDeliveryDays}
								/>
							</div>
						) : null}
					</div>
				</CollapsibleContent>
			</Collapsible>
		)}

		<div className="mt-0.5">
					<PricingSummary
						total={effectiveTotal}
						payInFullAmount={payInFullAmountBase}
						payInFullSavings={payInFullSavings}
						eligibleForPremiumMemberDiscount={eligibleForPremiumMemberDiscount}
						premiumMemberPackageDiscountAmount={premiumMemberPackageDiscountAmount}
						installmentAmount={installmentAmount}
					installmentCount={installmentCount}
					firstInstallmentAmount={firstInstallmentAmountBase}
						selectedOption={selectedPaymentOption}
						onSelectOption={setSelectedPaymentOption}
						trialAvailable={trialAvailable}
						currency={currency}
						templateLicenseAmount={templateLicenseAmount}
						selectedPackagePrice={
							Math.round((packageTotal + addonTotal) * 100) / 100
						}
						selectedPackagePriceAfterDiscount={packagePriceAfterPromo}
						packages={storefrontPackages}
						selectedPackageId={selectedPackageId}
						onSelectPackage={setSelectedPackageId}
						hostingPillPlans={serviceNeedsHosting ? hostingPillPlans : []}
						hostingCustomPlanRequired={hostingExceedsProMax}
						selectedHostingPlanId={selectedHostingPlanId}
						onSelectHostingPlan={setSelectedHostingPlanId}
						hostingBillingCycle={hostingBillingCycle}
						onHostingBillingCycleChange={setHostingBillingCycle}
						totalHostingEligibleCount={totalHostingEligibleCountForThisService}
						showLimitedOfferBadge={isInOfferCampaignWindow}
						limitedOfferDiscountPercent={limitedOfferDiscountPercent}
						limitedOfferPackageDiscountAmount={limitedOfferPackageDiscountAmount}
						limitedOfferBadgeDiscountAmount={selectedPackageDiscountAmount}
						limitedOfferShowEnded={limitedOfferShowEndedBadge}
						totalBeforeLimitedOffer={
							limitedOfferDiscountPercent !== undefined
								? totalBeforeLimitedOfferRow
								: undefined
						}
						subtotalAmount={totalAmount}
						promoEmail={session?.user?.email ?? undefined}
						promoPackageAmount={packageAfterLimited}
						promoLicenseAmount={templateLicenseLineAmount}
						promoHostingAmount={hostingPriceInSummary}
						promoPackageTiers={packageTierForPromo ? [packageTierForPromo] : []}
						promoOrderAmount={
							selectedPaymentOption === "full" ? payInFullAmountBase : effectiveTotal
						}
						appliedPromos={appliedPromos}
						onPromosChange={setAppliedPromos}
					/>
			</div>
			{/* eslint-disable-next-line react/no-unescaped-entities */}
					<div className="flex flex-col gap-4 pt-4 text-secondary-foreground sm:pt-5">
						<Button
							type="submit"
							id={addToBasketButtonId}
							className="custom-box-shadow h-14 rounded-full text-sm font-semibold"
							{...getCtaDataAttrs({
								ctaId: "add_to_basket_package",
								ctaName: "Add to Basket",
								ctaLocation: "service_package_card",
							})}
						>
							Add to Basket
						</Button>
						<Button
							type="submit"
							id="checkout"
							className="custom-box-shadow h-14 rounded-full bg-tertiary text-sm font-semibold hover:bg-tertiary/80"
							{...getCtaDataAttrs({
								ctaId:
									selectedPaymentOption === "trial"
										? "start_14_day_free_trial_package"
										: limitedOfferDiscountPercent !== undefined
											? "get_launch_offer_package"
											: "buy_now_package",
								ctaName:
									selectedPaymentOption === "trial"
										? `Start your free trial (${formatCurrency(0, currency)} today)`
										: `Place your order (${formatCurrencyExact(payNowAmount, currency)})`,
								ctaLocation: "service_package_card",
							})}
						>
							{selectedPaymentOption === "trial"
								? `Start your free trial (${formatCurrency(0, currency)} today)`
								: `Place your order (${formatCurrencyExact(payNowAmount, currency)})`}
						</Button>
						<div className="flex flex-col">
							<p className="px-1 text-center text-[10px] leading-snug text-grey-foreground/85 dark:text-muted-foreground sm:text-[11px]">
								{selectedPaymentOption === "trial"
									? "Cancel at any time."
									: "30-day money-back guarantee."}
							</p>
							<p className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 px-1 text-center text-[10px] leading-snug text-grey-foreground/85 dark:text-muted-foreground sm:text-[11px]">
								<span>0% interest finance available with</span>
								<img
									src="/assets/payment/Klarna_Logo.png"
									alt=""
									width={72}
									height={22}
									className="inline-block h-4 w-auto max-h-5 shrink-0 object-contain align-middle sm:h-[18px]"
								/>
								<span>at checkout.</span>
								<span>Payments are handled by</span>
								<img
									src="/assets/payment/Stripe_Logo.png"
									alt="Stripe"
									width={56}
									height={17}
									className="inline-block h-3 w-auto max-h-4 shrink-0 object-contain align-middle sm:h-3.5"
								/>
							</p>
						</div>
					</div>
				</div>
			</div>
		</form>
		<ServiceSignupModal
			open={showSignupModal}
			onOpenChange={setShowSignupModal}
			returnUrl={signupReturnUrl}
			onVerified={startServicePageCheckout}
		/>
		</>
	);
}

type PackageFeatureListProps = { features: PackageFeature[]; hideHeading?: boolean };

export function PackageFeatureList({ features, hideHeading = false }: PackageFeatureListProps) {
	const positive = features.filter((feature) => feature.status === FeatureStatus.RIGHT);
	const negative = features.filter((feature) => feature.status === FeatureStatus.WRONG);
	const neutral = features.filter((feature) => feature.status === FeatureStatus.NONE);

	return (
		<div>
			{hideHeading ? null : (
				<span className="text-sm font-medium text-secondary">This includes:</span>
			)}
			{/* eslint-disable-next-line react/no-unescaped-entities */}
			<div
				className={cn(
					"space-y-1 text-sm text-grey-foreground dark:text-muted-foreground",
					!hideHeading && "mt-2",
				)}
			>
				{positive.map((feature) => (
					<PackageFeatureItem key={feature.id} feature={feature} />
				))}
			</div>

			{negative.length > 0 && (
				<div className="mt-2 space-y-1 text-sm text-grey-foreground dark:text-muted-foreground">
					{negative.map((feature) => (
						<PackageFeatureItem key={feature.id} feature={feature} />
					))}
				</div>
			)}

			{neutral.length > 0 && (
				<div className="mt-2 space-y-1 text-sm text-grey-foreground dark:text-muted-foreground">
					{neutral.map((feature) => (
						<PackageFeatureItem key={feature.id} feature={feature} />
					))}
				</div>
			)}
		</div>
	);
}

type PackageFeatureItemProps = { feature: PackageFeature };

function PackageFeatureItem({ feature }: PackageFeatureItemProps) {
	const description = feature.description?.replace(/\s+/g, " ").trim();

    return (
        <div className="grid grid-cols-[1.75rem_1fr_auto] items-center gap-x-2">
			<span className="inline-flex size-7 items-center justify-center">
				{feature.status === FeatureStatus.RIGHT && (
					<Check className="size-4 text-secondary" strokeWidth={3} />
				)}
				{feature.status === FeatureStatus.WRONG && (
					<XIcon
						className="size-4 text-grey-foreground dark:text-muted-foreground"
						strokeWidth={3}
						aria-hidden
					/>
				)}
				{feature.status === FeatureStatus.NONE && (
					<span className="text-xs font-semibold text-grey-foreground dark:text-muted-foreground">--</span>
				)}
			</span>
			<span className="text-sm text-grey-foreground dark:text-muted-foreground">{feature.name}</span>
		{description ? (
			<PricingSummaryInfoDialog
				ariaLabel={`Feature information: ${feature.name}`}
				title={feature.name}
				ctaLocation="service_package_card"
			>
				{description}
			</PricingSummaryInfoDialog>
			) : (
				<span className="size-6" />
			)}
		</div>
	);
}

type ExpressDeliveryToggleProps = {
	expressAddon?: Addon;
	selectedAddons: SelectedAddon[];
	onChange: (addonId: string, type: AddonInputType, value: SelectedAddon["value"]) => void;
	/** 50% of the selected package `dueDays` (whole days, half-to-even ties); substitutes `{time}` or `{estimated delivery days of the package}` in `expressAddon.unit` when present. */
	expressDeliveryDays?: number | null;
};

export function ExpressDeliveryToggle({
	expressAddon,
	selectedAddons,
	onChange,
	expressDeliveryDays,
}: ExpressDeliveryToggleProps) {
	if (!expressAddon) return null;

	const active = selectedAddons.some((addon) => addon.id === expressAddon._id);
	const currency = useCurrency();
	const addonAmount = expressAddon.amounts?.[currency] ?? expressAddon.amount ?? 0;
	const priceStr = formatCurrency(addonAmount, currency);

	const unitTemplate = expressAddon.unit?.trim() ?? "";
	const descRaw = expressAddon.description?.trim() ?? "";
	const timeToken =
		typeof expressDeliveryDays === "number" &&
		Number.isFinite(expressDeliveryDays) &&
		expressDeliveryDays >= 0
			? String(expressDeliveryDays)
			: "";

	const hasExpressTimePlaceholder =
		/\{time\}/i.test(unitTemplate) ||
		/\{estimated delivery days of the package\}/i.test(unitTemplate);

	const heading = hasExpressTimePlaceholder
		? interpolateAddonDisplayTags(unitTemplate, {
				time: timeToken || "{time}",
				estimatedDeliveryDays: timeToken || "{estimated delivery days of the package}",
			})
		: timeToken
			? `Express delivery within ${timeToken} days`
			: "Express delivery";

	const subline = /\{price\}/i.test(descRaw)
		? interpolateAddonDisplayTags(descRaw, { price: priceStr })
		: `Add ${priceStr} to fast-track your project`;

	return (
		<div className="flex items-center justify-between rounded-18 bg-[#07A8BC]/10 px-4 py-3 dark:bg-core-midnight">
			<div className="space-y-1 text-xsm">
				<p className="font-semibold text-secondary">{heading}</p>
				<span className="text-xxsm text-grey-foreground/70">{subline}</span>
			</div>
			<Switch
				id={`express-${expressAddon._id}`}
				checked={active}
				onCheckedChange={(checked) => onChange(expressAddon._id, expressAddon.inputType, checked)}
			/>
		</div>
	);
}

type AdditionalAddonListProps = {
	addons: Addon[];
	selectedAddons: SelectedAddon[];
	onChange: (addonId: string, type: AddonInputType, value: SelectedAddon["value"]) => void;
	currency: Currency;
};

export function AdditionalAddonList({ addons, selectedAddons, onChange, currency }: AdditionalAddonListProps) {
	if (addons.length === 0) return null;

	return (
		<div className="mt-1 space-y-3">
			<ul className="space-y-4">
				{addons.map((addon) => {
					const addonAmount = addon.amounts?.[currency] ?? addon.amount ?? 0;
					return (
						<li
							key={addon._id}
							className="grid grid-cols-[1fr_auto] items-center justify-items-end rounded-18 bg-[#07A8BC]/10 px-4 py-3 dark:bg-core-midnight"
						>
							<div className="flex flex-col justify-self-start space-y-1 text-xsm">
								<p className="font-semibold leading-tight text-secondary">{addon.description}</p>
								<span className="text-xxsm text-grey-foreground/70">
									{formatNumberAddonPerUnitSubline(addon, addonAmount, currency)}
								</span>
							</div>
							<PackageAddonInput
								addon={addon}
								onChange={(value) => onChange(addon._id, addon.inputType, value)}
								defaultValue={
									selectedAddons.find((selected) => selected.id === addon._id)?.value
								}
							/>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

type PricingSummaryProps = {
	total: number;
	payInFullAmount: number;
	payInFullSavings: number;
	/** Logged-in user with active premium subscription — drives premium discount row. */
	eligibleForPremiumMemberDiscount: boolean;
	/** Notional member discount on list package price (display). */
	premiumMemberPackageDiscountAmount: number;
	installmentAmount: number;
	installmentCount: number;
	firstInstallmentAmount: number;
	selectedOption: "full" | "installments" | "trial";
	onSelectOption: (option: "full" | "installments" | "trial") => void;
	/** Show 14-day free trial option (active limited-time offer). */
	trialAvailable?: boolean;
	currency: Currency;
	/** Envato regular license list price in the active storefront currency (when set on the listing). */
	templateLicenseAmount?: number;
	/** Package tab price including selected add-ons. */
	selectedPackagePrice: number;
	/** Package + add-ons after member / LTO / pay-in-full (when selected). */
	selectedPackagePriceAfterDiscount: number;
	/** Packages for pills under the Package line (same selection as top tabs). */
	packages: Pick<Package, "_id" | "title">[];
	selectedPackageId: string;
	onSelectPackage: (packageId: string) => void;
	/** Published generic hosting plans shown as stacked options (Starter / Growth / Pro). */
	hostingPillPlans: Plan[];
	/** When true, show contact-support message instead of plan picker (>8 sites). */
	hostingCustomPlanRequired?: boolean;
	selectedHostingPlanId: string | null;
	onSelectHostingPlan: (planId: string) => void;
	hostingBillingCycle: HostingBillingCycle;
	onHostingBillingCycleChange: (cycle: HostingBillingCycle) => void;
	totalHostingEligibleCount: number;
	showLimitedOfferBadge?: boolean;
	limitedOfferDiscountPercent?: number;
	limitedOfferPackageDiscountAmount?: number;
	limitedOfferBadgeDiscountAmount?: number;
	/** Red pill copy when the session countdown hit zero but the promo window is still open */
	limitedOfferShowEnded?: boolean;
	/** Pre-offer total (e.g. after premium % only); shown struck-through when limited % applies */
	totalBeforeLimitedOffer?: number;
	/** Package + selected add-ons + license + hosting before discounts. */
	subtotalAmount: number;
	promoEmail?: string;
	promoPackageAmount: number;
	promoLicenseAmount: number;
	promoHostingAmount: number;
	promoPackageTiers: import("@/lib/promo-code").PromoCodePackageTier[];
	promoOrderAmount: number;
	appliedPromos: AppliedPromoCode[];
	onPromosChange: (promos: AppliedPromoCode[]) => void;
};

function PricingSummary({
	total,
	payInFullAmount,
	payInFullSavings,
	eligibleForPremiumMemberDiscount,
	premiumMemberPackageDiscountAmount,
	installmentAmount,
	installmentCount,
	firstInstallmentAmount,
	selectedOption,
	onSelectOption,
	trialAvailable = false,
	currency,
	templateLicenseAmount,
	selectedPackagePrice,
	selectedPackagePriceAfterDiscount,
	packages,
	selectedPackageId,
	onSelectPackage,
	hostingPillPlans,
	hostingCustomPlanRequired = false,
	selectedHostingPlanId,
	onSelectHostingPlan,
	hostingBillingCycle,
	onHostingBillingCycleChange,
	totalHostingEligibleCount,
	showLimitedOfferBadge = false,
	limitedOfferDiscountPercent,
	limitedOfferPackageDiscountAmount = 0,
	limitedOfferBadgeDiscountAmount = 0,
	limitedOfferShowEnded = false,
	totalBeforeLimitedOffer,
	subtotalAmount,
	promoEmail,
	promoPackageAmount,
	promoLicenseAmount,
	promoHostingAmount,
	promoPackageTiers,
	promoOrderAmount,
	appliedPromos,
	onPromosChange,
}: PricingSummaryProps) {
	const promoOff = Math.min(
		Math.max(
			0,
			appliedPromos.reduce((s, p) => s + Math.max(0, p.discountAmount), 0),
		),
		selectedOption === "full" ? payInFullAmount : total,
	);
	const displayTotal = Math.round(Math.max(0, total - promoOff) * 100) / 100;
	const displayPayInFullAmount =
		Math.round(Math.max(0, payInFullAmount - promoOff) * 100) / 100;
	const { dueToday: displayFirstInstallmentAmount, payLater: remainingAmount } =
		applyPromosToInstallments(firstInstallmentAmount, total, appliedPromos);
	const remainingInstallmentAmount =
		installmentCount > 1 ? remainingAmount / (installmentCount - 1) : remainingAmount;
	const remainingPayments = Math.max(installmentCount - 1, 0);
	const discountedSubtotalAmount =
		selectedOption === "full" ? displayPayInFullAmount : displayTotal;
	const showPackageDiscountedPrice =
		Math.ceil(selectedPackagePrice) > Math.ceil(selectedPackagePriceAfterDiscount);
	const showSubtotalDiscountedPrice =
		Math.ceil(subtotalAmount) > Math.ceil(discountedSubtotalAmount);
	const hostingYearlyPricingAvailable = useMemo(
		() =>
			hostingPillPlans.some(
				(p) => getHostingPlanPriceForDuration(p, currency, UserPlan.yearly) !== undefined,
			),
		[hostingPillPlans, currency],
	);
	const selectedHostingPlanForHeader = useMemo(
		() =>
			hostingPillPlans.find((p) => p._id === selectedHostingPlanId) ?? hostingPillPlans[0],
		[hostingPillPlans, selectedHostingPlanId],
	);
	const hostingHeaderDuration =
		hostingBillingCycle === "yearly" ? UserPlan.yearly : UserPlan.monthly;
	const hostingHeaderAmount = selectedHostingPlanForHeader
		? getHostingPlanPriceForDuration(
				selectedHostingPlanForHeader,
				currency,
				hostingHeaderDuration,
			)
		: undefined;
	const hostingHeaderSuffix = hostingBillingCycle === "yearly" ? "/y" : "/m";
	const alignSummaryRowsWithPackagePill = packages.length > 0;
	const selectedTotalDisplayAmount =
		selectedOption === "full" ? displayPayInFullAmount : displayTotal;
	const limitedOfferBadgeDisplayDiscountAmount =
		totalBeforeLimitedOffer !== undefined &&
		limitedOfferDiscountPercent !== undefined &&
		totalBeforeLimitedOffer > selectedTotalDisplayAmount + 0.001
			? Math.max(0, Math.ceil(totalBeforeLimitedOffer) - Math.ceil(selectedTotalDisplayAmount))
			: limitedOfferBadgeDiscountAmount;
	const limitedOfferBadge = showLimitedOfferBadge ? (
		<div className="mt-1 flex justify-end">
			<div
				className={cn(
					"inline-flex -mr-[15px] sm:-mr-5 items-center whitespace-nowrap rounded-l-full rounded-r-none px-4 py-1.5 text-[10px] font-semibold uppercase leading-tight tracking-[0.06em] text-white shadow-sm sm:text-[11px]",
					limitedOfferShowEnded
						? "bg-[#c5cbd4] [text-shadow:0_1px_1px_rgba(0,0,0,0.35)] dark:bg-zinc-500"
						: "bg-[#ef3c4e]",
				)}
			>
				<span>
					{limitedOfferShowEnded
						? "OFFER ENDED"
						: limitedOfferBadgeDisplayDiscountAmount > 0
							? `-${formatCurrency(limitedOfferBadgeDisplayDiscountAmount, currency)} OFF - LIMITED OFFER`
							: "LIMITED OFFER"}
				</span>
			</div>
		</div>
	) : null;
	return (
		<div className="flex w-full flex-col gap-3">
			<Collapsible defaultOpen={false} className="w-full min-w-0">
				<CollapsibleTrigger
					type="button"
					className="group flex w-full items-center justify-between gap-2 py-0.5 text-left"
				>
					<span className="text-sm font-medium text-secondary">Price details</span>
					<ChevronDown className="size-4 shrink-0 text-secondary transition-transform duration-200 group-data-[state=open]:rotate-180" />
				</CollapsibleTrigger>
				<CollapsibleContent className="overflow-hidden">
					<div className="mt-1 overflow-hidden rounded-[18px] bg-[#f3f8f7] p-[15px] dark:bg-core-midnight sm:p-5">
						<div className="mb-3">
							{packages.length > 0 ? (
								<div
									className="flex w-full min-w-0 gap-0.5 rounded-full bg-[#e9eef2] p-[2px] dark:bg-[#080F14]"
									role="radiogroup"
									aria-label="Choose package"
								>
									{packages.map((pkg) => {
										const active = pkg._id === selectedPackageId;
										return (
											<button
												key={pkg._id}
												type="button"
												role="radio"
												aria-checked={active}
												onClick={() => onSelectPackage(pkg._id)}
												className={cn(
													"flex min-h-[clamp(1.55rem,7.4vw,2rem)] min-w-0 flex-1 items-center justify-center rounded-full px-[clamp(0.25rem,1.8vw,0.5rem)] py-0.5 text-[clamp(0.625rem,2.9vw,0.75rem)] font-normal leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-1 sm:min-h-8 sm:px-2 sm:py-1 sm:text-xs",
													active
														? "border border-tertiary bg-white text-secondary shadow-sm dark:bg-card"
														: "text-grey-foreground dark:text-muted-foreground",
												)}
											>
												<span className="truncate text-center">{pkg.title}</span>
											</button>
										);
									})}
								</div>
							) : null}
							<div className="flex flex-col gap-0.5">
								<div
									className={cn(
										"flex min-w-0 items-center justify-between gap-2 text-sm font-semibold text-grey-foreground dark:text-foreground",
										packages.length > 0 && "mt-2",
										alignSummaryRowsWithPackagePill && "pl-2 sm:pl-2.5",
									)}
								>
									<div className="inline-flex min-w-0 items-center gap-1.5">
										<span className="min-w-0 truncate text-sm font-medium leading-tight text-secondary">
											Package
										</span>
										<PricingSummaryInfoDialog
											ariaLabel="About selected package"
											title="Package"
											iconSize="sm"
											preWrap
											ctaLocation="service_package_card"
										>
											{PACKAGE_INFO}
										</PricingSummaryInfoDialog>
										<span className="shrink-0 whitespace-nowrap text-[10px] font-normal leading-tight text-grey-foreground/85 dark:text-muted-foreground sm:text-[11px]">
											(one-time fee)
										</span>
									</div>
									{showPackageDiscountedPrice ? (
										<span className="inline-flex shrink-0 items-baseline gap-1.5 tabular-nums leading-tight">
											<span className="text-sm font-semibold text-secondary line-through decoration-secondary/80">
												{formatCurrency(selectedPackagePrice, currency)}
											</span>
											<span className="text-sm font-bold text-[#ef3c4e]">
												{formatCurrency(selectedPackagePriceAfterDiscount, currency)}
											</span>
										</span>
									) : (
										<span className="shrink-0 text-sm font-semibold tabular-nums leading-tight text-secondary">
											{formatCurrency(selectedPackagePrice, currency)}
										</span>
									)}
								</div>
							</div>
						</div>
						{templateLicenseAmount !== undefined && templateLicenseAmount > 0 ? (
							<div
								className={cn(
									"flex min-w-0 items-center justify-between gap-2 text-sm font-semibold text-grey-foreground dark:text-foreground",
									alignSummaryRowsWithPackagePill && "pl-2 sm:pl-2.5",
									hostingPillPlans.length > 0 || hostingCustomPlanRequired ? "mb-2" : "mb-3",
								)}
							>
								<div className="inline-flex min-w-0 items-center gap-1.5">
									<span className="min-w-0 truncate text-sm font-medium leading-tight text-secondary">
										License
									</span>
									<PricingSummaryInfoDialog
										ariaLabel="About license"
										title="License"
										iconSize="sm"
										ctaLocation="service_package_card"
									>
										{LICENSE_PRICE_INFO}
									</PricingSummaryInfoDialog>
									<span className="shrink-0 whitespace-nowrap text-[10px] font-normal leading-tight text-grey-foreground/85 dark:text-muted-foreground sm:text-[11px]">
										(one-time fee)
									</span>
								</div>
								<span className="shrink-0 text-sm font-semibold tabular-nums leading-tight text-secondary">
									{formatCurrency(templateLicenseAmount, currency)}
								</span>
							</div>
						) : null}
						<div className="flex flex-col gap-2">
							{hostingCustomPlanRequired ? (
								<div className="space-y-3 border-t border-border-visible pt-3">
									<div className="flex min-w-0 items-center justify-between gap-2 text-sm font-semibold leading-snug text-grey-foreground dark:text-foreground">
										<div className="inline-flex min-w-0 items-center gap-1.5">
											<span className="min-w-0 truncate text-sm font-medium uppercase text-secondary">
												Hosting
											</span>
											<PricingSummaryInfoDialog
												ariaLabel="About hosting plans"
												title="Hosting plans"
												iconSize="sm"
												ctaLocation="service_package_card"
											>
												{HOSTING_INFO}
											</PricingSummaryInfoDialog>
										</div>
										<span className="shrink-0 text-sm font-semibold tabular-nums leading-tight text-secondary">
											—
										</span>
									</div>
									<p className="px-1 text-center text-xs font-normal leading-snug text-grey-foreground/90 dark:text-muted-foreground">
										{HOSTING_CUSTOM_PLAN_SUPPORT_MESSAGE}
									</p>
								</div>
							) : hostingPillPlans.length > 0 ? (
								<div className="space-y-3 border-t border-border-visible pt-3">
									<div className="flex min-w-0 items-center justify-between gap-2 text-sm font-semibold leading-snug text-grey-foreground dark:text-foreground">
										<div className="inline-flex min-w-0 items-center gap-1.5">
											<span className="min-w-0 truncate text-sm font-medium uppercase text-secondary">
												Hosting
											</span>
											<PricingSummaryInfoDialog
												ariaLabel="About hosting plans"
												title="Hosting plans"
												iconSize="sm"
												ctaLocation="service_package_card"
											>
												{HOSTING_INFO}
											</PricingSummaryInfoDialog>
											<span className="shrink-0 whitespace-nowrap text-[10px] font-normal leading-tight text-grey-foreground/85 dark:text-muted-foreground sm:text-[11px]">
												({hostingBillingCycle === "yearly" ? "billed yearly" : "billed monthly"})
											</span>
										</div>
										{hostingHeaderAmount !== undefined ? (
											<span className="inline-flex shrink-0 items-center gap-x-1 leading-tight">
												<span className="whitespace-nowrap text-sm font-semibold tabular-nums text-secondary">
													{formatCurrency(hostingHeaderAmount, currency)}
												</span>
												<span className="whitespace-nowrap text-xs font-normal tabular-nums text-secondary">
													{hostingHeaderSuffix}
												</span>
											</span>
										) : (
											<span className="shrink-0 text-sm font-semibold tabular-nums leading-tight text-secondary">
												—
											</span>
										)}
									</div>
									<HostingBillingCycleToggle
										value={hostingBillingCycle}
										onChange={onHostingBillingCycleChange}
										yearlyAvailable={hostingYearlyPricingAvailable}
									/>
									<div
										className="mb-2 grid w-full min-w-0 gap-3"
										role="radiogroup"
										aria-label="Choose hosting plan"
									>
										{hostingPillPlans.map((plan) => (
											<HostingPlanOptionButton
												key={plan._id}
												plan={plan}
												selected={plan._id === selectedHostingPlanId}
												onSelect={() => onSelectHostingPlan(plan._id)}
												currency={currency}
												billingCycle={hostingBillingCycle}
												disabled={isHostingPlanBlockedForUsage(plan, totalHostingEligibleCount)}
												footerNotice={
													isHostingPlanBlockedForUsage(plan, totalHostingEligibleCount)
														? totalHostingEligibleCount > 8
															? "This plan covers up to 8 websites only."
															: getHostingPlanTier(plan) === "growth"
																? "This plan covers up to 3 websites only."
																: "This plan covers 1 website only."
														: undefined
												}
											/>
										))}
									</div>
								</div>
							) : null}
							<div className="border-t border-border-visible pt-3">
								<div className="flex min-w-0 items-center justify-between gap-2 text-sm font-medium leading-snug text-grey-foreground dark:text-foreground">
									<span className="min-w-0 truncate text-sm font-medium uppercase text-secondary">
										Subtotal
									</span>
									{showSubtotalDiscountedPrice ? (
										<span className="inline-flex shrink-0 items-baseline gap-1.5 whitespace-nowrap tabular-nums">
											<span className="text-base font-medium text-secondary line-through decoration-secondary/80">
												{formatCurrency(subtotalAmount, currency)}
											</span>
											<span className="text-base font-bold text-[#ef3c4e]">
												{formatCurrency(discountedSubtotalAmount, currency)}
											</span>
										</span>
									) : (
										<span className="whitespace-nowrap text-base font-medium tabular-nums text-secondary">
											{formatCurrency(subtotalAmount, currency)}
										</span>
									)}
								</div>
							</div>
						</div>
					</div>
				</CollapsibleContent>
			</Collapsible>

			<div
				id="service-payment-options"
				className="flex scroll-mt-[7.5rem] flex-col gap-3 overflow-hidden rounded-[18px] bg-[#f3f8f7] p-[15px] dark:bg-core-midnight sm:p-5"
			>
				<div>
					<span className="block text-sm font-medium uppercase text-secondary">Payment Options</span>
				</div>
				<div className="grid w-full min-w-0 gap-3">
					{trialAvailable ? (
						<PaymentOptionButton
							label="14-day free trial"
							leftPrefix="Due today"
							sublabel="Pay rest in 14 days"
							sublabelInfo={FREE_TRIAL_14_DAYS_INFO}
							amount={0}
							displayAmount={`${formatCurrency(0, currency)}\n1 x ${formatCurrency(displayTotal, currency)}`}
							savings={0}
							selected={selectedOption === "trial"}
							onSelect={() => {
								if (selectedOption === "trial") return;
								ga4CtaClick({
									cta_id: "free_trial_14_days_payment",
									cta_name: "14-day free trial",
									cta_location: "service_package_payment",
								});
								onSelectOption("trial");
							}}
							currency={currency}
						/>
					) : (
						<PaymentOptionButton
							label="Pay an advance"
							leftPrefix="Due today"
							sublabel={
								installmentCount === 2
									? "Pay rest next month"
									: `Pay over next ${remainingPayments} months`
							}
							sublabelInfo={
								installmentCount === 2 ? PAY_REST_NEXT_MONTH_INFO : PAY_OVER_NEXT_TWO_MONTHS_INFO
							}
							amount={displayFirstInstallmentAmount}
							displayAmount={`${formatCurrencyExact(displayFirstInstallmentAmount, currency)}\n${remainingPayments} x ${formatCurrencyExact(remainingInstallmentAmount, currency)}`}
							savings={0}
							selected={selectedOption === "installments"}
							onSelect={() => {
								if (selectedOption === "installments") return;
								ga4CtaClick({
									cta_id: "pay_an_advance_payment",
									cta_name: "Pay an advance",
									cta_location: "service_package_payment",
								});
								onSelectOption("installments");
							}}
							currency={currency}
						/>
					)}
					<PaymentOptionButton
						label="Pay in full"
						sublabel="Save 10%"
						sublabelInfo={SAVE_TEN_PERCENT_PAY_IN_FULL_INFO}
						amount={displayPayInFullAmount}
						strikethroughAmount={subtotalAmount}
						savings={payInFullSavings}
						selected={selectedOption === "full"}
						onSelect={() => {
							if (selectedOption === "full") return;
							ga4CtaClick({
								cta_id: "pay_in_full_payment",
								cta_name: "Pay in full",
								cta_location: "service_package_payment",
							});
							onSelectOption("full");
						}}
						currency={currency}
					/>
				</div>

				<div className="mt-1 border-t border-border-visible pt-3">
				<div className="flex flex-col gap-1">
					<div className="inline-flex min-w-0 items-center gap-1.5">
						<span className="min-w-0 truncate text-sm font-medium uppercase text-secondary">
							Discounts
						</span>
						<PricingSummaryInfoDialog
							ariaLabel="About discounts"
							title="Discounts"
							iconSize="sm"
							ctaLocation="service_package_card"
						>
							{DISCOUNTS_INFO}
						</PricingSummaryInfoDialog>
					</div>
					{payInFullSavings > 0 ? (
						<>
							<div
								className={cn(
									"flex min-w-0 items-center justify-between gap-2 text-xs leading-snug",
									selectedOption === "full"
										? "font-medium text-secondary"
										: "font-normal text-grey-foreground/70 dark:text-muted-foreground",
								)}
							>
								<span className="min-w-0 pl-3">
									<span
										className={cn(
											"mr-1",
											selectedOption === "full" ? "opacity-80" : "opacity-70",
										)}
										aria-hidden
									>
										•
									</span>
									Pay in full (10% off)
								</span>
								<span
									className={cn(
										"shrink-0 whitespace-nowrap tabular-nums",
										selectedOption === "full" ? "font-semibold" : "font-normal",
									)}
								>
									{selectedOption === "full"
										? `-${formatCurrency(payInFullSavings, currency)}`
										: "N/A"}
								</span>
							</div>
							<div
								className={cn(
									"flex min-w-0 items-center justify-between gap-2 text-xs leading-snug",
									eligibleForPremiumMemberDiscount
										? "font-medium text-secondary"
										: "font-normal text-grey-foreground/70 dark:text-muted-foreground",
								)}
							>
								<span className="min-w-0 pl-3">
									<span
										className={cn(
											"mr-1",
											eligibleForPremiumMemberDiscount ? "opacity-80" : "opacity-70",
										)}
										aria-hidden
									>
										•
									</span>
									Premium member (10% off)
								</span>
								<span
									className={cn(
										"shrink-0 whitespace-nowrap tabular-nums",
										eligibleForPremiumMemberDiscount ? "font-semibold" : "font-normal",
									)}
								>
									{eligibleForPremiumMemberDiscount
										? `-${formatCurrency(premiumMemberPackageDiscountAmount, currency)}`
										: "N/A"}
								</span>
							</div>
							{limitedOfferDiscountPercent !== undefined &&
							limitedOfferPackageDiscountAmount > 0 ? (
								<div className="flex min-w-0 items-center justify-between gap-2 text-xs font-medium leading-snug text-[#ef3c4e]">
									<span className="min-w-0 pl-3">
										<span className="mr-1 opacity-80" aria-hidden>
											•
										</span>
										Limited time offer ({limitedOfferDiscountPercent}% off)
									</span>
									<span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">
										-{formatCurrency(limitedOfferPackageDiscountAmount, currency)}
									</span>
								</div>
							) : null}
						</>
					) : null}
					<div className="pt-1">
						<PromoCodeRedeemField
							currency={currency}
							email={promoEmail}
							packageAmount={promoPackageAmount}
							licenseAmount={promoLicenseAmount}
							hostingAmount={promoHostingAmount}
							packageTiers={promoPackageTiers}
							orderAmount={promoOrderAmount}
							applied={appliedPromos}
							onChange={onPromosChange}
						/>
					</div>
					<div className="my-2 border-t border-border-visible" aria-hidden />
					{selectedOption === "full" ? (
						<>
							<div className="flex min-w-0 items-center justify-between gap-2 text-sm font-medium leading-snug text-grey-foreground dark:text-foreground">
								<span className="min-w-0 truncate text-sm font-medium uppercase text-secondary">TOTAL</span>
								{totalBeforeLimitedOffer !== undefined &&
								limitedOfferDiscountPercent !== undefined &&
								totalBeforeLimitedOffer > displayPayInFullAmount + 0.001 ? (
									<div className="flex min-w-0 flex-shrink-0 items-baseline justify-end gap-2">
										<span className="inline-block text-[17px] font-normal not-italic leading-tight text-secondary tabular-nums line-through decoration-solid">
											{formatCurrency(totalBeforeLimitedOffer, currency)}
										</span>
										<span className="whitespace-nowrap text-lg font-bold tabular-nums text-[#ef3c4e]">
											{formatCurrencyExact(displayPayInFullAmount, currency)}
										</span>
									</div>
								) : (
									<span className="whitespace-nowrap text-base font-medium tabular-nums text-secondary">
										{formatCurrencyExact(displayPayInFullAmount, currency)}
									</span>
								)}
							</div>
							{limitedOfferBadge}
							<div className="flex flex-col items-end gap-1">
								<div className="flex flex-row items-baseline justify-end gap-2 whitespace-nowrap text-sm font-normal text-grey-foreground/70 dark:text-muted-foreground">
									<span>Due today</span>
									<span className="whitespace-nowrap text-lg font-bold tabular-nums text-tertiary">
										{formatCurrencyExact(displayPayInFullAmount, currency)}
									</span>
								</div>
							</div>
						</>
					) : selectedOption === "trial" ? (
						<>
							<div className="flex min-w-0 items-center justify-between gap-2 text-sm font-medium leading-snug text-grey-foreground dark:text-foreground">
								<span className="min-w-0 truncate text-sm font-medium uppercase text-secondary">TOTAL</span>
								{totalBeforeLimitedOffer !== undefined &&
								limitedOfferDiscountPercent !== undefined &&
								totalBeforeLimitedOffer > displayTotal + 0.001 ? (
									<div className="flex min-w-0 flex-shrink-0 items-baseline justify-end gap-2">
										<span className="inline-block text-[17px] font-normal not-italic leading-tight text-secondary tabular-nums line-through decoration-solid">
											{formatCurrency(totalBeforeLimitedOffer, currency)}
										</span>
										<span className="whitespace-nowrap text-lg font-bold tabular-nums text-[#ef3c4e]">
											{formatCurrencyExact(displayTotal, currency)}
										</span>
									</div>
								) : (
									<span className="whitespace-nowrap text-base font-medium tabular-nums text-secondary">
										{formatCurrencyExact(displayTotal, currency)}
									</span>
								)}
							</div>
							{limitedOfferBadge}
							<div className="flex flex-col items-end gap-1">
								<div className="flex flex-row items-baseline justify-end gap-2 whitespace-nowrap text-sm font-normal text-grey-foreground/70 dark:text-muted-foreground">
									<span>Due in 14 days</span>
									<span className="whitespace-nowrap text-base font-medium tabular-nums text-secondary">
										{formatCurrencyExact(displayTotal, currency)}
									</span>
								</div>
								<div className="flex flex-row items-baseline justify-end gap-2 whitespace-nowrap text-sm font-normal text-grey-foreground/70 dark:text-muted-foreground">
									<span>Due today</span>
									<span className="whitespace-nowrap text-lg font-bold tabular-nums text-tertiary">
										{formatCurrencyExact(0, currency)}
									</span>
								</div>
							</div>
						</>
					) : (
						<>
							<div className="flex min-w-0 items-center justify-between gap-2 text-sm font-medium leading-snug text-grey-foreground dark:text-foreground">
								<span className="min-w-0 truncate text-sm font-medium uppercase text-secondary">TOTAL</span>
								{totalBeforeLimitedOffer !== undefined &&
								limitedOfferDiscountPercent !== undefined &&
								totalBeforeLimitedOffer > displayTotal + 0.001 ? (
									<div className="flex min-w-0 flex-shrink-0 items-baseline justify-end gap-2">
										<span className="inline-block text-[17px] font-normal not-italic leading-tight text-secondary tabular-nums line-through decoration-solid">
											{formatCurrency(totalBeforeLimitedOffer, currency)}
										</span>
										<span className="whitespace-nowrap text-lg font-bold tabular-nums text-[#ef3c4e]">
											{formatCurrencyExact(displayTotal, currency)}
										</span>
									</div>
								) : (
									<span className="whitespace-nowrap text-base font-medium tabular-nums text-secondary">
										{formatCurrencyExact(displayTotal, currency)}
									</span>
								)}
							</div>
							{limitedOfferBadge}
							<div className="flex flex-col items-end gap-1">
								<div className="flex flex-row items-baseline justify-end gap-2 whitespace-nowrap text-sm font-normal text-grey-foreground/70 dark:text-muted-foreground">
									<span>Pay later</span>
									<span className="whitespace-nowrap text-base font-medium tabular-nums text-secondary">
										{formatCurrencyExact(remainingAmount, currency)}
									</span>
								</div>
								<div className="flex flex-row items-baseline justify-end gap-2 whitespace-nowrap text-sm font-normal text-grey-foreground/70 dark:text-muted-foreground">
									<span>Due today</span>
									<span className="whitespace-nowrap text-lg font-bold tabular-nums text-tertiary">
										{formatCurrencyExact(displayFirstInstallmentAmount, currency)}
									</span>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
			</div>
		</div>
	);
}

type PaymentOptionButtonProps = {
	label: string;
	/** Shown to the left of the price in the right column (e.g. "Due today") */
	leftPrefix?: string;
	sublabel?: string;
	/** Shown in an info dialog next to the sublabel (installment / pay-in-full copy). */
	sublabelInfo?: string;
	amount?: number;
	displayAmount?: string;
	/** Original total before pay-in-full package discount; shown struck-through when applicable. */
	strikethroughAmount?: number;
	savings: number;
	selected: boolean;
	onSelect: () => void;
	currency: Currency;
};

function PaymentOptionButton({
	label,
	leftPrefix,
	sublabel,
	sublabelInfo,
	amount,
	displayAmount,
	strikethroughAmount,
	savings,
	selected,
	onSelect,
	currency,
}: PaymentOptionButtonProps) {
	return (
		<div
			role="button"
			tabIndex={0}
			aria-pressed={selected}
			onClick={onSelect}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onSelect();
				}
			}}
			className={cn(
				"group flex w-full min-w-0 max-w-full cursor-pointer items-center justify-between gap-2 overflow-hidden rounded-[26px] border py-4 pl-2 pr-[15px] text-left transition-all sm:gap-3 sm:pl-2.5 sm:pr-5",
				selected
					? "border-tertiary bg-white text-secondary shadow-[0_14px_28px_rgba(23,199,184,0.25)] dark:bg-card dark:text-secondary"
					: "border-transparent bg-[#e9eef2] text-grey-foreground hover:border-[#dfe4e9] hover:bg-white dark:bg-[#080F14] dark:text-muted-foreground dark:hover:bg-core-midnight",
			)}
		>
			<div className="flex min-w-0 flex-1 items-center gap-3">
				<span
					className={cn(
						"flex size-4 shrink-0 items-center justify-center rounded-full border aspect-square",
						selected ? "border-tertiary bg-tertiary" : "border-grey/30 bg-white dark:bg-black",
					)}
				/>
				<div
					className={cn(
						"flex min-w-0 flex-col text-sm font-semibold",
						selected ? "text-secondary" : "text-grey-foreground dark:text-muted-foreground",
					)}
				>
					<span className="break-words">{label}</span>
					{sublabel ? (
						<span className="inline-flex min-w-0 flex-wrap items-center gap-1 text-xxsm font-normal text-grey-foreground/70 dark:text-muted-foreground">
							{sublabel}
							{sublabelInfo ? (
								<PricingSummaryInfoDialog
									ariaLabel={`More about ${label}`}
									title={label}
									iconSize="sm"
									stopPropagation
									ctaLocation="service_package_card"
								>
									{sublabelInfo}
								</PricingSummaryInfoDialog>
							) : null}
						</span>
					) : null}
				</div>
			</div>
			<div
				className={cn(
					"flex min-w-0 shrink-0 text-right text-sm font-semibold",
					selected ? "text-secondary" : "text-grey-foreground dark:text-muted-foreground",
				)}
			>
				{displayAmount ? (
					<div className="flex flex-col items-end">
						{displayAmount.split("\n").map((line, index) => {
							const isMainPrice =
								index === 0 &&
								(line.startsWith("£") ||
									line.startsWith("$") ||
									line.startsWith("€") ||
									line.startsWith("CA$") ||
									line.startsWith("A$"));
							if (isMainPrice) {
								return (
									<div key={index} className="flex flex-wrap items-center justify-end gap-1 sm:gap-2">
										{leftPrefix ? (
											<span className="whitespace-nowrap text-xxsm font-normal text-grey-foreground/70 dark:text-muted-foreground">
												{leftPrefix}
											</span>
										) : null}
										<span
											className={cn(
												"whitespace-nowrap text-sm font-semibold",
												selected ? "text-secondary" : "text-grey-foreground dark:text-muted-foreground",
											)}
										>
											{line}
										</span>
									</div>
								);
							}
							return (
								<span
									key={index}
									className="block whitespace-nowrap text-xxsm font-normal text-grey-foreground/70 dark:text-muted-foreground"
								>
									{line}
								</span>
							);
						})}
					</div>
				) : (
					<div className="flex flex-col items-end">
						<div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
							{strikethroughAmount != null &&
							savings > 0 &&
							strikethroughAmount > (amount ?? 0) ? (
								<span className="whitespace-nowrap text-xs font-normal text-grey-foreground/70 line-through dark:text-muted-foreground">
									{formatCurrency(strikethroughAmount, currency)}
								</span>
							) : null}
							<span
								className={cn(
									"whitespace-nowrap text-sm font-semibold",
									selected ? "text-secondary" : "text-grey-foreground dark:text-muted-foreground",
								)}
							>
								{formatCurrencyExact(amount ?? 0, currency)}
							</span>
						</div>
						{savings > 0 ? (
							<span className="whitespace-nowrap text-xxsm font-normal text-grey-foreground/70 dark:text-muted-foreground">
								You save{" "}
								<span className="font-normal text-tertiary">{formatCurrency(savings, currency)}</span>
							</span>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
}

type PackageAddonInputProps = {
	addon: Pick<Addon, "_id" | "inputType" | "unit" | "limit">;
	defaultValue?: SelectedAddon["value"];
	onChange: (arg0: SelectedAddon["value"]) => void;
	className?: string;
};

export function PackageAddonInput({
	addon,
	onChange,
	defaultValue,
	className,
}: PackageAddonInputProps) {
	const inputType =
		isToggleInputType(addon.inputType)
			? AddonInputType.toggle
			: isNumberInputType(addon.inputType)
				? AddonInputType.number
				: addon.inputType;

	switch (inputType) {
		case AddonInputType.toggle:
			if (defaultValue !== undefined && typeof defaultValue !== "boolean") {
				throw new Error("defaultValue requires to be of type 'boolean'");
			}

			return (
				<Switch
					id={`addon-${addon._id}`}
					defaultChecked={defaultValue}
					onCheckedChange={(checked) => onChange?.call(undefined, checked)}
				/>
			);
		case AddonInputType.number:
			if (defaultValue !== undefined && typeof defaultValue !== "number") {
				throw new Error("defaultValue requires to be of type 'number'");
			}

			return (
				<PackageAddonNumberInput
					addon={addon}
					defaultValue={defaultValue}
					onChange={onChange}
					className={className}
				/>
			);
		default:
			return null;
	}
}

function PackageAddonNumberInput({
	addon,
	onChange,
	defaultValue,
	className,
}: Omit<PackageAddonInputProps, "defaultValue"> & { defaultValue?: number }) {
	const [error, setError] = useState<string | undefined>();
	const [value, setValue] = useState<number>(defaultValue ?? 0);

	const handleChange = (newValue: number) => {
		const clampedValue = Math.max(0, Math.min(newValue, addon.limit ?? Infinity));
		setValue(clampedValue);
		
		if (clampedValue > (addon.limit ?? Infinity)) {
			setError(`Maximum ${addon.limit}`);
		} else if (error !== undefined) {
			setError(undefined);
		}

		onChange?.(clampedValue);
	};

	const increment = () => {
		handleChange(value + 1);
	};

	const decrement = () => {
		handleChange(value - 1);
	};

	return (
		<div className="relative">
			<Input
				id={`addon-${addon._id}`}
				type="number"
				placeholder={addon.unit}
				min={0}
				max={addon.limit}
				value={value}
				className={cn(
					"h-8 w-24 pr-8 text-center text-xs placeholder:text-xs bg-white dark:bg-black text-[#07A8BC]",
					className,
					error !== undefined && "border-red-500",
				)}
				onChange={(e) => {
					const val = Number.parseInt(e.target.value) || 0;
					handleChange(val);
				}}
			/>
			<div className="absolute right-1 top-0 flex h-full flex-col items-center justify-center">
				<button
					type="button"
					onClick={increment}
					disabled={value >= (addon.limit ?? Infinity)}
					className="flex h-1/2 items-center justify-center p-0.5 hover:opacity-70 disabled:opacity-30"
				>
					<ChevronUp className="size-3 text-[#07A8BC]" />
				</button>
				<button
					type="button"
					onClick={decrement}
					disabled={value <= 0}
					className="flex h-1/2 items-center justify-center p-0.5 hover:opacity-70 disabled:opacity-30"
				>
					<ChevronDown className="size-3 text-[#07A8BC]" />
				</button>
			</div>
			{error !== undefined && (
				<span className="absolute left-1 top-[2.125rem] text-xs text-red-500">{error}</span>
			)}
		</div>
	);
}

type PackageRadioCardProps = {
	total: number;
	isPremiumPlan: boolean;
};

export function PackagePlanRadioCard({ isPremiumPlan, total }: PackageRadioCardProps) {
	const id = useId();
	const currency = useCurrency();
	const planName = isPremiumPlan ? "Premium" : "Basic";
	const calculatedTotal = isPremiumPlan ? total * (1 - Discount / 100) : total;
	const discountedValue = isPremiumPlan ? total - calculatedTotal : 0;

	return (
		<label
			htmlFor={id}
			className={clsx(
				"custom-box-shadow flex h-24 cursor-pointer items-center gap-4 rounded-full px-8 lg:h-28 lg:gap-8",
				"transition-opacity duration-300 [&:has([data-state='unchecked'])]:opacity-50",
				isPremiumPlan ? "bg-tertiary/10" : "bg-grey/5",
			)}
		>
			<RadioGroupItem2 id={id} value={planName} className="absolute" />
			<div className="grid w-full grid-cols-2 items-center justify-items-center gap-x-2 gap-y-1 text-sm lg:text-sm2">
				<span className="col-span-full text-sm font-light">
					with {planName} plan you pay
				</span>

				<div className="col-span-full inline-flex items-center justify-center gap-2">
					{isPremiumPlan && (
						<div className="space-x-1">
							<span className="text-sm line-through">
								{formatCurrency(total, currency)}
							</span>
							<span className="text-sm text-red-600 ">-{Discount}%</span>
						</div>
					)}
					<div
						className={clsx(
							"inline-flex h-6 items-center justify-center rounded bg-grey/5 px-2 font-semibold lg:h-8",
							isPremiumPlan && "font-semibold",
						)}
					>
						{formatCurrency(calculatedTotal, currency)}
					</div>
				</div>

				<span className="col-span-full mt-1 text-xs text-grey-foreground ">
					You save{" "}
					<span className="text-tertiary">
						{formatCurrency(discountedValue, currency)}
					</span>
				</span>
			</div>
		</label>
	);
}
